import { ClickAwayListener } from "@mui/material";
import {
  ObjectParam,
  StringParam,
  useParam,
} from "@redotech/react-router-util/param";
import { IterableMap, genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { isControlKeyPressed } from "@redotech/react-util/html-events";
import {
  LoadState,
  useLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import { useScrolled } from "@redotech/react-util/scroll";
import { useSearch } from "@redotech/react-util/search";
import { Translate, useTranslation } from "@redotech/redo-i18n";
import RightArrowIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import ColumnsIcon from "@redotech/redo-icons/columns-03.svg";
import DuplicateIcon from "@redotech/redo-icons/copy-01.svg";
import DownloadIconSvg from "@redotech/redo-icons/download-01.svg";
import DownloadCloudIconSvg from "@redotech/redo-icons/download-cloud-01.svg";
import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import PinFilledIcon from "@redotech/redo-icons/pin-02_filled.svg";
import PinIcon from "@redotech/redo-icons/pin.svg";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import TriangleRight from "@redotech/redo-icons/triangle-right_filled.svg";
import XCloseIconSvg from "@redotech/redo-icons/x-close.svg";
import {
  AdvancedFilterData,
  FilterGroup,
  filtersAreEqual,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  AdvancedFilterType,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import { filterGroupsAreEqual } from "@redotech/redo-views-common/utils/util";
import {
  BaseView,
  VALID_PAGE_SIZES,
  ValidPageSize,
  ViewColumn,
  ViewSort,
  viewTypes,
} from "@redotech/redo-views-common/views";
import { asyncIterableToArray } from "@redotech/util/async-iterator";
import { objectEqual, stringEqual } from "@redotech/util/equal";
import { deepCopyObject } from "@redotech/util/object";
import { downloadBlob } from "@redotech/web-util/download";
import classnames from "classnames";
import { stringify } from "csv-stringify/browser/esm/sync";
import Fuse from "fuse.js";
import * as React from "react";
import {
  ForwardedRef,
  MouseEvent,
  MutableRefObject,
  ReactElement,
  ReactNode,
  RefObject,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import {
  AdvancedTableFilter,
  decodeAdvancedFilterGroup,
  decodeAdvancedFilters,
  encodeAdvancedFilterGroup,
  encodeAdvancedFilters,
  withDefaultFilterValue,
} from "./advanced-filters/advanced-filter";
import { ArrayStringFilterGroup } from "./advanced-filters/components/array-to-string-filter";
import { BooleanFilterGroup } from "./advanced-filters/components/boolean-filter";
import { DateFilterGroup } from "./advanced-filters/components/date-filter";
import {
  applyDeferredFilterUpdate,
  applyDeferredFilterUpdateToGroup,
} from "./advanced-filters/components/deferred-filter-commit";
import { ExistsFilterGroup } from "./advanced-filters/components/exists-filter";
import { FilterFieldSubmenu } from "./advanced-filters/components/filter-field-submenu";
import {
  addFilterToGroup,
  createEmptyFilterGroup,
  filtersToGroup,
} from "./advanced-filters/components/filter-group-builder";
import { FilterGroupChip } from "./advanced-filters/components/filter-group-chip";
import { canCascadeIntoFilterValueEditor } from "./advanced-filters/components/filter-value-editor";
import { NumberFilterGroup } from "./advanced-filters/components/number-filter";
import { StringFilterGroup } from "./advanced-filters/components/string-filter";
import { RedoBadge } from "./arbiter-components/badge/redo-badge";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "./arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "./arbiter-components/command-menu/redo-command-menu";
import { AllFiltersModal } from "./arbiter-components/filter-overflow/all-filters-modal";
import { FilterChipsList } from "./arbiter-components/filter-overflow/filter-chips-list";
import { FilterOverflowContainer } from "./arbiter-components/filter-overflow/filter-overflow-container";
import { TableFiltersModal } from "./arbiter-components/filter-overflow/table-filters-modal";
import { BaseRedoInput } from "./arbiter-components/input/base-redo-text-input";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { RedoListItem } from "./arbiter-components/list/redo-list";
import { RedoListDropdown } from "./arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "./arbiter-components/list/redo-multiselect-dropdown";
import {
  RedoTable,
  RedoTableColumn,
  RedoTableLoadingStateContent,
  RedoTableSize,
} from "./arbiter-components/tables/redo-table";
import { RedoTableCellProps } from "./arbiter-components/tables/redo-table-cells";
import * as redoTableCellsCss from "./arbiter-components/tables/redo-table-cells.module.css";
import {
  RedoTableColumnResizer,
  getResizedColumnWidth,
} from "./arbiter-components/tables/redo-table-column-resizer";
import {
  StandardRedoTableHeaderCell,
  StandardRedoTableHeaderCellArrowMode,
} from "./arbiter-components/tables/redo-table-header-cells";
import * as redoTableHeaderCellsCss from "./arbiter-components/tables/redo-table-header-cells.module.css";
import * as redoTableRowCss from "./arbiter-components/tables/redo-table-row.module.css";
import * as redoTableCss from "./arbiter-components/tables/redo-table.module.css";
import { RedoVirtualTable } from "./arbiter-components/tables/redo-virtual-table";
import * as redoVirtualTableCss from "./arbiter-components/tables/redo-virtual-table.module.css";
import { RedoHorizontalTabs } from "./arbiter-components/tabs/redo-horizontal-tabs";
import { Tooltip } from "./arbiter-components/tooltip/tooltip";
import { ColumnSelector } from "./column-selector";
import { SortableItem } from "./components/ui/sortable";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipTrigger,
} from "./components/ui/tooltip";
import { DeleteTableViewModal } from "./delete-table-view-modal";
import { Dropdown } from "./dropdown";
import { Flex } from "./flex";
import ChevronDown from "./icon-old/chevron-down.svg";
import TrashIcon from "./icon-old/trash.svg";
import { HeaderOverrideContext } from "./page";
import { SaveTableViewModal } from "./save-table-view-modal";
import { Spinner } from "./spinner";
import {
  Column,
  ColumnAlignment,
  CsvColumn,
  DEFAULT_PAGE_SIZE,
  Filter,
  FilterComponent,
  RowCounts,
  Search,
  TableContentProps,
  TableFetcher,
  TableProps,
  TableRef,
  TableTheme,
  sortEqual,
  sortParam,
} from "./table";
import { TableFilterEditorHeader } from "./table-filter-editor-header";
import * as tableCss from "./table.module.css";
import { useTable2ColumnReordering } from "./table2-column-header-draggable";
import { isColumnReorderable } from "./table2-column-reorder";
import {
  type DisplayedColumnsActiveViewSyncState,
  reconcileDisplayedColumns,
  shouldSkipDisplayedColumnsActiveViewSync,
} from "./table2-displayed-columns-sync";
import { useViewScopedDebounce } from "./table2-view-scoped-debounce";
import * as table2Css from "./table2.module.css";
import { TabsPortal } from "./tabs-portal";
import { Text } from "./text";
import { variableToSemanticDisplay } from "./utils/display-code-variables";

function getFilterDisplayName(
  component: Pick<FilterComponent, "name" | "getDisplayName">,
): string {
  return (
    component.getDisplayName?.() ||
    variableToSemanticDisplay(component.name) ||
    ""
  );
}

export interface DisplayedColumn {
  key: string;
  displayed: boolean;
}
const ExpandHeader = memo(function ExpandHeader({
  expandedRows,
  setAllExpanded,
}: {
  expandedRows: Record<number, boolean>;
  setAllExpanded: (expanded: boolean) => void;
}) {
  const isSomeExpanded = useMemo(() => {
    return Object.values(expandedRows).some(Boolean);
  }, [expandedRows]);

  return (
    <Flex
      align="center"
      className={classnames(
        table2Css.expandIcon,
        isSomeExpanded && table2Css.expanded,
      )}
      justify="center"
      onClick={() => {
        if (isSomeExpanded) {
          setAllExpanded(false);
        } else {
          setAllExpanded(true);
        }
      }}
      pl="xs"
      w="full"
    >
      <TriangleRight />
    </Flex>
  );
});

function getExpanderColumn<T>(
  expander: ({ row }: { row: T }) => {
    numExpansions: number;
    expandable: boolean;
  },
  expandedRows: Record<number, boolean>,
  toggleExpansion: (rowIndex: number) => void,
  setAllExpanded: (expanded: boolean) => void,
): Column2<T> {
  return {
    alignment: ColumnAlignment.CENTER,
    key: "expander",
    title: (
      <ExpandHeader
        expandedRows={expandedRows}
        setAllExpanded={setAllExpanded}
      />
    ),
    renderNormalCell: ({ row, rowIndex }: { row: T; rowIndex: number }) => {
      const { numExpansions, expandable } = expander({ row });
      if (numExpansions === 0 || !expandable) {
        return null;
      }

      const isExpanded = expandedRows[rowIndex];

      return (
        <Flex
          align="center"
          className={classnames(
            table2Css.expandIcon,
            isExpanded && table2Css.expanded,
          )}
          justify="center"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            toggleExpansion(rowIndex);
          }}
          pl="xs"
          w="full"
        >
          <TriangleRight />
        </Flex>
      );
    },
    width: 40,
    widthMode: { type: "fixed", width: "40px" },
  };
}

export function getAdvancedFilterArray(
  activeView: viewTypes[keyof viewTypes] | undefined,
  advancedFilters: AdvancedTableFilter[] | undefined,
) {
  return encodeAdvancedFilters(
    (activeView?.filters || []).reduce((acc, filter) => {
      if (!advancedFilters) {
        return acc;
      }
      let activeViewFilter = advancedFilters.find(
        (activeViewFilter) => activeViewFilter.data.name === filter.name,
      );
      if (!activeViewFilter) {
        for (const f of advancedFilters) {
          const sub = f.subFilters?.find((sf) => sf.data.name === filter.name);
          if (sub) {
            activeViewFilter = sub;
            break;
          }
        }
      }
      if (activeViewFilter) {
        const deepClone = deepCopyObject(activeViewFilter);
        deepClone.data = filter;
        acc.push(deepClone);
      }
      return acc;
    }, [] as AdvancedTableFilter[]),
  );
}

function getColumnsFromDisplayedColumns<T>(
  columns: Column2<T>[],
  displayedColumns: DisplayedColumn[],
): Column2<T>[] {
  const returnCols: Column2<T>[] = [];
  for (const displayedColumn of displayedColumns) {
    const column = columns.find((column) => column.key === displayedColumn.key);
    if (column) {
      column.hidden = !displayedColumn.displayed;
      returnCols.push(column);
    }
  }

  return returnCols;
}

const AdvancedFilterComponent = memo(function AdvancedFilterComponent({
  filter,
  setValue,
  removeFilter,
  filters,
  isViewFilter,
}: {
  filter: AdvancedTableFilter;
  removeFilter: () => void;
  setValue: (filter: AdvancedTableFilter) => void;
  filters: AdvancedTableFilter[];
  isViewFilter: boolean;
}) {
  switch (filter.type) {
    case AdvancedFilterType.DATE:
      return (
        <DateFilterGroup
          filter={filter}
          openOnRender={
            filter.openOnRender ? filter.openOnRender : !isViewFilter
          }
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    case AdvancedFilterType.ARRAY_TO_STRING:
      return (
        <ArrayStringFilterGroup
          filter={filter}
          filters={filters}
          openOnRender={
            filter.openOnRender ? filter.openOnRender : !isViewFilter
          }
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    case AdvancedFilterType.NUMBER:
      return (
        <NumberFilterGroup
          filter={filter}
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    case AdvancedFilterType.STRING:
      return (
        <StringFilterGroup
          filter={filter}
          filters={filters}
          openOnRender={filter.openOnRender || !isViewFilter}
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    case AdvancedFilterType.BOOLEAN:
      return (
        <BooleanFilterGroup
          filter={filter}
          openOnRender={filter.openOnRender || !isViewFilter}
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    case AdvancedFilterType.EXISTS:
      return (
        <ExistsFilterGroup
          filter={filter}
          openOnRender={filter.openOnRender || !isViewFilter}
          removeFilter={removeFilter}
          setFilter={setValue}
        />
      );
    default:
      return null;
  }
});

/**
 * Test id for an add-filter menu item, matching the legacy filter dropdown's
 * `advanced-filter-<slug>` selector so existing E2E page objects keep working
 * when a table opts into the deferred-pill cascade.
 */
function advancedFilterTestId(filter: AdvancedTableFilter): string {
  return `advanced-filter-${filter.displayName.toLowerCase().replace(/\s+/g, "-")}`;
}

function viewChanges<T>({
  activeView,
  displayedAdvancedFilters,
  displayedFilterGroup,
  enableFilterGroups,
  columnWidths,
  columnPins,
  displayedColumns,
  pageSize,
  sort,
  columns,
}: {
  activeView?: viewTypes[keyof viewTypes];
  displayedAdvancedFilters: AdvancedTableFilter[];
  displayedFilterGroup?: FilterGroup;
  enableFilterGroups?: boolean;
  columnWidths: Record<string, number>;
  columnPins: Record<string, boolean>;
  displayedColumns: DisplayedColumn[];
  pageSize?: number;
  sort: TableSort<string>;
  columns: Column2<T>[];
}): {
  allFiltersMatch: boolean;
  filterGroupMatches: boolean;
  columnsValuesMatch: boolean;
  pageSizesMatch: boolean;
  sortsMatch: boolean;
  columnOrderMatches: boolean;
} {
  const allFiltersMatch =
    displayedAdvancedFilters.length === activeView?.filters.length &&
    displayedAdvancedFilters.every((f) =>
      activeView?.filters.some((vf) => filtersAreEqual(f.data, vf)),
    );

  const filterGroupMatches = enableFilterGroups
    ? filterGroupsAreEqual(
        displayedFilterGroup ?? undefined,
        activeView?.filterGroup ?? undefined,
      )
    : true;

  const columnsForDisplay = getColumnsFromDisplayedColumns<T>(
    columns,
    displayedColumns,
  );

  const columnKeys = new Set(columns.map((c) => c.key));
  const activeViewColumns = (activeView?.columns || []).filter((c) =>
    columnKeys.has(c.key),
  );

  let columnsValuesMatch = true;
  for (const viewColumn of activeViewColumns) {
    const col = columnsForDisplay.find((col) => col.key === viewColumn.key);
    if (!col) {
      columnsValuesMatch = false;
      break;
    }
    const currentPinned = columnPins[col.key] ?? false;
    const viewPinned = viewColumn.pinned ?? false;
    if (
      viewColumn.hidden != col.hidden ||
      viewColumn.width !== (columnWidths?.[col.key] ?? col.width) ||
      viewPinned !== currentPinned
    ) {
      columnsValuesMatch = false;
      break;
    }
  }

  const viewColKeys = activeViewColumns.map((col) => col.key);
  const displayedColKeys = columnsForDisplay
    .map((col) => col.key)
    .filter((key) => viewColKeys.includes(key));

  let columnOrderMatches = true;
  for (const [i, viewColKey] of viewColKeys.entries()) {
    if (viewColKey !== displayedColKeys[i]) {
      columnOrderMatches = false;
      break;
    }
  }

  const pageSizesMatch = activeView?.pageSize === pageSize;
  const sortsMatch =
    activeView?.sort?.direction === sort.direction &&
    activeView?.sort?.key === sort.key;

  return {
    allFiltersMatch,
    filterGroupMatches,
    columnsValuesMatch,
    pageSizesMatch,
    sortsMatch,
    columnOrderMatches,
  };
}

export const aggregationType = [
  "sum",
  "average",
  "min",
  "max",
  "count",
] as const;
export type AggregationType = (typeof aggregationType)[number];

export interface ColumnAggregation<DATA> {
  /** Function to extract a numeric value from a row for aggregation */
  getValue: (row: DATA) => number;
  /** The type of aggregation to perform */
  type: AggregationType;
  /** Optional function to format the aggregated value for display */
  formatValue?: (value: number) => string;
}

/** Calculate the aggregated value for a column based on its aggregation config */
export function calculateAggregation<DATA>(
  rows: DATA[],
  aggregation: ColumnAggregation<DATA>,
): number {
  if (rows.length === 0) {
    return 0;
  }

  const values = rows.map((row) => aggregation.getValue(row));

  switch (aggregation.type) {
    case "sum":
      return values.reduce((acc, val) => acc + val, 0);
    case "average":
      return values.reduce((acc, val) => acc + val, 0) / values.length;
    case "min":
      return Math.min(...values);
    case "max":
      return Math.max(...values);
    case "count":
      return values.length;
    default:
      return 0;
  }
}

/** Get a human-readable label for an aggregation type */
function getAggregationLabel(t: Translate, type: AggregationType): string {
  switch (type) {
    case "sum":
      return t("Sum");
    case "average":
      return t("Avg", { context: "abbreviation of average" });
    case "min":
      return t("Min", { context: "abbreviation of minimum" });
    case "max":
      return t("Max", { context: "abbreviation of maximum" });
    case "count":
      return t("Count");
    default:
      return "";
  }
}

/** Row component that displays aggregated values at the bottom of the table */
const AggregationCells = genericMemo(function AggregationRow<T>({
  columns,
  items,
  columnWidths,
}: {
  columns: Column2<T>[];
  items: T[];
  columnWidths: Record<string, number>;
}) {
  const { t } = useTranslation();
  const hasAnyAggregation = columns.some((col) => col.aggregation);

  if (!hasAnyAggregation || items.length === 0) {
    return null;
  }

  const calculateStickyLeftOffset = (columnIndex: number): number => {
    return columns
      .slice(0, columnIndex)
      .filter((c) => c.sticky)
      .reduce((acc, c) => {
        const width = columnWidths[c.key];
        if (width) {
          return acc + width;
        }
        if (c.widthMode?.type === "fixed") {
          return (
            acc +
            (typeof c.widthMode.width === "number"
              ? c.widthMode.width
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                parseInt(c.widthMode.width as string) || 0)
          );
        }
        if (
          c.widthMode?.type === "fill" &&
          c.widthMode.minLength !== undefined
        ) {
          return (
            acc +
            (typeof c.widthMode.minLength === "number"
              ? c.widthMode.minLength
              : parseInt(c.widthMode.minLength as string) || 0)
          );
        }
        return acc;
      }, 0);
  };

  const cells = columns.map((column, idx) => {
    const alignmentClass = redoTableRowCss.left;
    const stickyLeftOffset = column.sticky
      ? calculateStickyLeftOffset(idx)
      : undefined;

    if (!column.aggregation) {
      return (
        <div
          className={classnames(
            table2Css.aggregationCell,
            redoTableCellsCss.cell,
            alignmentClass,
            column.sticky && redoVirtualTableCss.stickyAggregationCell,
          )}
          key={column.key ?? idx}
          style={
            stickyLeftOffset !== undefined
              ? { left: `${stickyLeftOffset}px` }
              : {}
          }
        />
      );
    }

    const value = calculateAggregation(items, column.aggregation);
    const formattedValue = column.aggregation.formatValue
      ? column.aggregation.formatValue(value)
      : value.toLocaleString();
    const label = getAggregationLabel(t, column.aggregation.type);

    return (
      <div
        className={classnames(
          table2Css.aggregationCell,
          redoTableCellsCss.cell,
          alignmentClass,
          column.sticky && redoVirtualTableCss.stickyAggregationCell,
        )}
        key={column.key ?? idx}
        style={
          stickyLeftOffset !== undefined
            ? { left: `${stickyLeftOffset}px` }
            : {}
        }
      >
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {label}:
        </Text>
        <Text fontSize="xs" fontWeight="semibold" textColor="primary">
          {formattedValue}
        </Text>
      </div>
    );
  });

  return <>{cells}</>;
});

const AggregationRow = genericMemo(function AggregationRow<T>({
  columns,
  items,
  columnWidths,
}: {
  columns: Column2<T>[];
  items: T[];
  columnWidths: Record<string, number>;
}) {
  const gridTemplateColumns = columns
    .map((col) => {
      const width = columnWidths[col.key];
      if (width) {
        return `${width}px`;
      }
      return col.widthMode?.type === "fixed"
        ? `${col.widthMode.width}px`
        : "1fr";
    })
    .join(" ");

  return (
    <div className={table2Css.aggregationRow} style={{ gridTemplateColumns }}>
      <AggregationCells
        columns={columns}
        columnWidths={columnWidths}
        items={items}
      />
    </div>
  );
});

/** Same as original columns, but using the new style of specifying headers and rows, _but_ it's always "fill" width mode
 * (because `redo/web/table.tsx` uses the widths to calculate certain things). */
export type Column2<DATA> = Omit<Column<DATA>, "Cell"> &
  Omit<RedoTableColumn<DATA>, "renderHeaderCell" | "alignment"> & {
    //"widthMode" |
    /** Determines if the column is hidden before the user does any column selection */
    hidden?: boolean;
    /** Determines if the column can be toggled between hidden and visible. If not set, column can be hidden */
    nonHidable?: boolean;
    /** Optional aggregation configuration for this column */
    aggregation?: ColumnAggregation<DATA>;
    /** When true, hides the pin button for this column */
    disableStickiness?: boolean;
    /** When true, prevents the column from being reordered when Table2 column reordering is enabled */
    disableColumnReorder?: boolean;
    /** When true, prevents the column from being resized by dragging the header border */
    disableColumnResize?: boolean;
    /** When true, allows a non-fill column to be resized by dragging the header border */
    enableColumnResize?: boolean;
    /**
     * Whether the column is available, evaluated at render time (unlike `hidden`). When false, it is
     * excluded from rendering and the column selector. Defaults to true.
     */
    available?: boolean;
  };

/**
 *  Presentational layer of the header of the fetching table.
 * Lays out the filters, CSV button, action button, etc...
 */
export const Table2Header = genericMemo(function Table2Header<T>({
  headerTitle,
  headerSubtitle,
  headerSideText,
  inlineHeaderTitle = false,
  primaryFilterOptions,
  dynamicFilters,
  countLoad,
  filters,
  setFilters,
  setPrimaryFilter,
  embeddedTable,
  filterComponents,
  primaryFilter,
  search,
  searchEnabled,
  searchPlaceholder,
  setSearch,
  searchButton,
  csv,
  hideExportButton,
  hideViewActionButtons = false,
  actionButton,
  downloadTriggerRef,
  fetcher,
  filename,
  onDownloadStatusChange,
  passThroughValues,
  saveView,
  saveViewLoad,
  searchRefreshSymbol,
  sort,
  setSort,
  columnSelection,
  columns,
  advancedFilters,
  advancedFilterParams,
  setAdvancedFilterParams,
  viewBeingEdited,
  setViewBeingEdited,
  activeView,
  onDeleteView,
  onExportRequested,
  exportButtonLeftShift,
  onlySaveView,
  viewNotificationsEnabled,
  focusSearchOnRender,
  reservedViewNames,
  appliedAdvancedFilters,
  pageSize,
  displayedColumns,
  setDisplayedColumns,
  columnWidths,
  columnPins,
  putTabsInHeader,
  customReportDropdownItems = [],
  getCsvColumns,
  aboveTableContent,
  filterInfo,
  filterButtonTrailingContent,
  enableFilterGroups,
  legacyFilterMenu,
  nestedFilterCascade,
  filterGroupParams,
  setFilterGroupParams,
  displayedFilterGroup,
  setDisplayedFilterGroup,
  toolbarEndPaddingRight,
  showEditViewButton: showEditViewButtonProp,
  showResetFilters = true,
  cardLayout = false,
}: TableProps2<T> & {
  countLoad: LoadState<RowCounts>;
  advancedFilterParams: string;
  setAdvancedFilterParams: (filters: string) => void;
  advancedFilters: AdvancedTableFilter[];
  filters: { [name: string]: string };
  setFilters: (filters: { [name: string]: string }) => void;
  primaryFilter: string;
  setPrimaryFilter: (filter: string) => void;
  filterComponents: FilterComponent[];
  searchEnabled: boolean;
  searchButton: ReactNode;
  search: string;
  setSearch: (search: string) => void;
  searchRefreshSymbol?: symbol;
  sort: TableSort<string>;
  setSort: (sort: TableSort<string>) => void;
  viewBeingEdited?: boolean;
  setViewBeingEdited?: (viewBeingEdited: boolean) => void;
  activeView?: viewTypes[keyof viewTypes];
  onDeleteView?: (view: viewTypes[keyof viewTypes]) => void;
  onlySaveView?: boolean;
  reservedViewNames?: Set<string>;
  focusSearchOnRender?: boolean;
  appliedAdvancedFilters?: AdvancedTableFilter[];
  setDisplayedColumns: (columns: DisplayedColumn[]) => void;
  displayedColumns: DisplayedColumn[];
  columnWidths: Record<string, number>;
  columnPins: Record<string, boolean>;
  customReportDropdownItems?: CustomReportDropdownItem[];
  filterGroupParams?: string;
  setFilterGroupParams?: (filterGroup: string) => void;
  displayedFilterGroup: FilterGroup;
  setDisplayedFilterGroup: (filterGroup: FilterGroup) => void;
}) {
  const [searchRef, setSearchRef] = useState<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  useEffect(() => {
    if (focusSearchOnRender && searchRef) {
      searchRef.focus();
    }
  }, [focusSearchOnRender, searchRef, activeView]);

  const firstSearchRender = useRef(true);
  useEffect(() => {
    if (searchRefreshSymbol && searchRef) {
      if (firstSearchRender.current) {
        firstSearchRender.current = false;
        return;
      }
      setSearch("");
      searchRef.focus();
    }
  }, [searchRefreshSymbol, searchRef, setSearch]);

  const [filtersDropdownButtonRef, setFiltersDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [
    columnSelectionDropdownButtonRef,
    setColumnSelectionDropdownButtonRef,
  ] = useState<HTMLButtonElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const [
    advancedFiltersDropdownButtonRef,
    setAdvancedFiltersDropdownButtonRef,
  ] = useState<HTMLButtonElement | null>(null);

  const [displayedAdvancedFilters, setDisplayedAdvancedFilters] = useState<
    AdvancedTableFilter[]
  >(decodeAdvancedFilters(advancedFilterParams, advancedFilters));

  // Sync filter group state with URL params
  // Only update if the decoded value is actually different to avoid unnecessary re-fetches
  useEffect(() => {
    if (!enableFilterGroups) {
      return;
    }
    if (filterGroupParams) {
      const decoded = decodeAdvancedFilterGroup(
        filterGroupParams,
        advancedFilters,
      );
      if (decoded && !filterGroupsAreEqual(decoded, displayedFilterGroup)) {
        setDisplayedFilterGroup(decoded);
      }
    } else if (advancedFilterParams) {
      // Fall back to converting flat filters
      const flatFilters = decodeAdvancedFilters(
        advancedFilterParams,
        advancedFilters,
      );
      const newGroup = filtersToGroup(flatFilters.map((f) => f.data));
      if (!filterGroupsAreEqual(newGroup, displayedFilterGroup)) {
        setDisplayedFilterGroup(newGroup);
      }
    } else if (
      !filterGroupsAreEqual(createEmptyFilterGroup(), displayedFilterGroup)
    ) {
      setDisplayedFilterGroup(createEmptyFilterGroup());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableFilterGroups,
    filterGroupParams,
    advancedFilterParams,
    advancedFilters,
    setDisplayedFilterGroup,
  ]);

  const [advancedFiltersDropdownOpen, setAdvancedFiltersDropdownOpen] =
    useState(false);

  const [filterSearchText, setFilterSearchText] = useState("");
  const [expandedSubFilter, setExpandedSubFilter] =
    useState<AdvancedTableFilter | null>(null);

  const [showAllFiltersModal, setShowAllFiltersModal] = useState(false);
  const [modalAddFilterButtonRef, setModalAddFilterButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [isFiltersOverflowing, setIsFiltersOverflowing] = useState(false);

  const filterSearcher = useMemo(() => {
    return new Fuse<AdvancedTableFilter>([], {
      keys: ["displayName"],
      threshold: 0.3,
    });
  }, []);

  const searchedFilters = useSearch(
    filterSearcher,
    advancedFilters,
    filterSearchText,
  );

  // Keeps the EXACT `data` reference, which committed-pill matching relies on. Reassigning `.data`
  // instead of spreading keeps the `AdvancedTableFilter` union correlated.
  const buildTableFilter = (
    def: AdvancedTableFilter,
    data: AdvancedFilterData,
  ): AdvancedTableFilter => {
    const copy = deepCopyObject(def);
    copy.data = data;
    return copy;
  };

  /**
   * Whether the cascade commits pills into `displayedFilterGroup` (nested)
   * rather than `displayedAdvancedFilters` (flat).
   */
  const nestedCascade = !!enableFilterGroups && !!nestedFilterCascade;

  const commitFilterGroup = useHandler((newFilterGroup: FilterGroup) => {
    setDisplayedFilterGroup(newFilterGroup);
    if (setFilterGroupParams) {
      setFilterGroupParams(encodeAdvancedFilterGroup(newFilterGroup));
    }
  });

  const commitDeferredFilter = useHandler(
    (def: AdvancedTableFilter, data: AdvancedFilterData) => {
      if (nestedCascade) {
        commitFilterGroup(addFilterToGroup(displayedFilterGroup, data));
        return;
      }
      const next = [...displayedAdvancedFilters, buildTableFilter(def, data)];
      setDisplayedAdvancedFilters(next);
      setAdvancedFilterParams(encodeAdvancedFilters(next));
    },
  );

  const updateDeferredFilter = useHandler(
    (
      def: AdvancedTableFilter,
      previous: AdvancedFilterData,
      nextData: AdvancedFilterData,
    ) => {
      if (nestedCascade) {
        commitFilterGroup(
          applyDeferredFilterUpdateToGroup(
            displayedFilterGroup,
            previous,
            nextData,
          ),
        );
        return;
      }
      const next = applyDeferredFilterUpdate(
        displayedAdvancedFilters,
        previous,
        () => buildTableFilter(def, nextData),
      );
      setDisplayedAdvancedFilters(next);
      setAdvancedFilterParams(encodeAdvancedFilters(next));
    },
  );

  /** Commit the field with no value chosen yet, so the pill can be filled in place. */
  const addZeroStateFilter = useHandler((def: AdvancedTableFilter) => {
    const data = withDefaultFilterValue(deepCopyObject(def.data));
    if (nestedCascade) {
      commitFilterGroup(addFilterToGroup(displayedFilterGroup, data));
      setAdvancedFiltersDropdownOpen(false);
      return;
    }
    const next = [...displayedAdvancedFilters, buildTableFilter(def, data)];
    setDisplayedAdvancedFilters(next);
    setAdvancedFilterParams(encodeAdvancedFilters(next));
    setAdvancedFiltersDropdownOpen(false);
  });

  const buildLeafItem = useHandler(
    (def: AdvancedTableFilter): RedoCommandMenuItem => {
      // Clicking the field always drops a zero-state pill; the submenu is the
      // hover shortcut for picking a value before the pill exists.
      const item: RedoCommandMenuItem = {
        id: def.data.name,
        text: def.displayName,
        Icon: def.Icon,
        dataTestId: advancedFilterTestId(def),
        onClick: () => addZeroStateFilter(def),
      };
      if (!canCascadeIntoFilterValueEditor(def)) {
        return item;
      }
      return {
        ...item,
        submenuPlacement: "right-start",
        keepSubmenuOpenOnLeave: true,
        customSubmenuContent: (anchor) => (
          <FilterFieldSubmenu
            anchor={anchor}
            closeMenu={() => setAdvancedFiltersDropdownOpen(false)}
            definitions={advancedFilters}
            initialData={deepCopyObject(def.data)}
            onCommit={(data) => commitDeferredFilter(def, data)}
            onUpdate={(previous, nextData) =>
              updateDeferredFilter(def, previous, nextData)
            }
            toTableFilter={(data) => buildTableFilter(def, data)}
          />
        ),
      };
    },
  );

  const addNestedGroup = useHandler(() => {
    commitFilterGroup({
      ...displayedFilterGroup,
      clauses: [...displayedFilterGroup.clauses, createEmptyFilterGroup()],
    });
    setAdvancedFiltersDropdownOpen(false);
  });

  const cascadeFilterItems = useMemo<RedoCommandMenuItem[]>(() => {
    const fieldItems = advancedFilters
      .filter((def) => !def.deprecated)
      .map((def) =>
        def.subFilters?.length
          ? {
              id: def.data.name,
              text: def.displayName,
              Icon: def.Icon,
              dataTestId: advancedFilterTestId(def),
              children: def.subFilters
                .filter((sub) => !sub.deprecated)
                .map(buildLeafItem),
            }
          : buildLeafItem(def),
      );
    if (!nestedCascade) {
      return fieldItems;
    }
    return [
      {
        id: "add-filter-group",
        text: t("Add filter group"),
        Icon: LayersIcon,
        dataTestId: "add-filter-group",
        onClick: addNestedGroup,
      },
      ...fieldItems,
    ];
  }, [advancedFilters, buildLeafItem, nestedCascade, addNestedGroup, t]);

  useEffect(() => {
    setDisplayedAdvancedFilters(
      decodeAdvancedFilters(advancedFilterParams, advancedFilters),
    );
  }, [advancedFilterParams, advancedFilters]);

  const shouldShowAdvancedFilterButton = advancedFilters.length > 0;
  const shouldShowFilterButton =
    filterComponents.length > 0 && !shouldShowAdvancedFilterButton;
  const shouldShowFilterRow =
    selectedFilters.length > 0 || displayedAdvancedFilters.length > 0;

  const xPadding = cardLayout ? undefined : "3xl";
  const filterBarBottomPadding = inlineHeaderTitle
    ? "none"
    : cardLayout
      ? "xl"
      : "3xl";

  const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);

  const deleteViewItem: RedoCommandMenuItem = useMemo(() => {
    return {
      Icon: TrashIcon,
      text: t("Delete view"),
      onClick: () => {
        setDeleteViewModalOpen(true);
      },
      theme: "destructive",
    };
  }, [setDeleteViewModalOpen, t]);

  const editViewItem: RedoCommandMenuItem = useMemo(() => {
    return {
      text: t("Edit view"),
      onClick: () => {
        setViewBeingEdited?.(true);
      },
      Icon: Edit04Icon,
    };
  }, [setViewBeingEdited, t]);

  const duplicateViewItem: RedoCommandMenuItem = useMemo(() => {
    return {
      Icon: DuplicateIcon,
      text: t("Duplicate view"),
      onClick: () => {
        setSaveViewModalOpen(true);
      },
    };
  }, [setSaveViewModalOpen, t]);

  const [editViewDropdownRef, setEditViewDropdownRef] =
    useState<HTMLElement | null>(null);
  const [editViewDropdownOpen, setEditViewDropdownOpen] = useState(false);

  const {
    allFiltersMatch,
    filterGroupMatches,
    columnsValuesMatch,
    pageSizesMatch,
    sortsMatch,
    columnOrderMatches,
  } = useMemo(
    () =>
      viewChanges<T>({
        activeView,
        displayedAdvancedFilters,
        displayedFilterGroup,
        enableFilterGroups,
        columnWidths,
        columnPins,
        displayedColumns,
        sort,
        pageSize,
        columns,
      }),
    [
      activeView,
      displayedAdvancedFilters,
      displayedFilterGroup,
      enableFilterGroups,
      columnWidths,
      columnPins,
      displayedColumns,
      sort,
      pageSize,
      columns,
    ],
  );

  const viewHasChanged =
    !allFiltersMatch ||
    !filterGroupMatches ||
    !columnsValuesMatch ||
    !pageSizesMatch ||
    !sortsMatch ||
    !columnOrderMatches;

  const showUpdateViewButton =
    viewHasChanged && !viewBeingEdited && !activeView?.isNotEditable;

  const showEditViewButton =
    !showUpdateViewButton &&
    !viewBeingEdited &&
    !activeView?.isNotEditable &&
    (showEditViewButtonProp ?? true);

  const showViewActionButtons =
    !!saveView &&
    activeView &&
    !!onDeleteView &&
    appliedAdvancedFilters &&
    !onlySaveView;

  const showSaveNewViewButton = viewHasChanged && !viewBeingEdited;

  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const saveViewHandler = useHandler(() => {
    // these will always be defined when this handler is called
    if (!activeView || !saveView) {
      return;
    }
    saveView({
      name: activeView.name,
      filters: displayedAdvancedFilters.map((filter) => filter.data),
      filterGroup: enableFilterGroups ? displayedFilterGroup : undefined,
      columns: getColumnsFromDisplayedColumns(columns, displayedColumns).map(
        (col) => ({
          key: col.key,
          width: columnWidths?.[col.key] ?? col.width,
          hidden: col?.hidden ?? false,
          pinned: columnPins?.[col.key] ?? false,
        }),
      ),
      sort,
      pageSize,
      _id: activeView._id,
      position: activeView.position,
      showNotification:
        viewNotificationsEnabled && "showNotification" in activeView
          ? activeView.showNotification
          : undefined,
      hideWhenEmpty:
        viewNotificationsEnabled && "hideWhenEmpty" in activeView
          ? activeView.hideWhenEmpty
          : undefined,
    });
  });

  const handleFilterButtonClick = useHandler(() => {
    if (isFiltersOverflowing) {
      setShowAllFiltersModal(true);
    } else {
      setAdvancedFiltersDropdownOpen(true);
    }
  });

  // Handler for filter group changes (when enableFilterGroups is true)
  const handleFilterGroupChange = commitFilterGroup;

  // Handler for adding a filter to the current group
  const handleAddFilterToGroup = useHandler((filter: AdvancedTableFilter) => {
    const newGroup = addFilterToGroup(displayedFilterGroup, filter.data);
    handleFilterGroupChange(newGroup);
    setAdvancedFiltersDropdownOpen(false);
    setFilterSearchText("");
  });

  // Handler for updating a specific clause in the filter group
  const handleClauseChange = useHandler(
    (index: number, newFilter: AdvancedTableFilter) => {
      const newClauses = [...displayedFilterGroup.clauses];
      newClauses[index] = newFilter.data;
      handleFilterGroupChange({ ...displayedFilterGroup, clauses: newClauses });
    },
  );

  // Handler for removing a specific clause from the filter group
  const handleClauseRemove = useHandler((index: number) => {
    const newClauses = displayedFilterGroup.clauses.filter(
      (_, i) => i !== index,
    );
    handleFilterGroupChange({ ...displayedFilterGroup, clauses: newClauses });
  });

  // Handler for updating a nested filter group clause
  const handleNestedGroupChange = useHandler(
    (index: number, newGroup: FilterGroup) => {
      const newClauses = [...displayedFilterGroup.clauses];
      newClauses[index] = newGroup;
      handleFilterGroupChange({ ...displayedFilterGroup, clauses: newClauses });
    },
  );

  // Handler for adding a new nested filter group
  const handleAddNestedGroup = addNestedGroup;

  // Render function for filters in the filter group builder
  const renderFilterForGroup = useHandler(
    ({
      filter,
      onFilterChange,
      onRemove,
      allFilters,
    }: {
      filter: AdvancedTableFilter;
      onFilterChange: (filter: AdvancedTableFilter) => void;
      onRemove: () => void;
      allFilters: AdvancedTableFilter[];
    }) => {
      return (
        <AdvancedFilterComponent
          filter={filter}
          filters={allFilters}
          isViewFilter
          removeFilter={onRemove}
          setValue={onFilterChange}
        />
      );
    },
  );

  // Sort clause indices: non-group filters before nested groups, preserving
  // original indices for mutations so data order is unchanged.
  // Matches the sort order in FilterGroupClauseList in filter-group-builder.tsx.
  const sortedClauseIndices = useMemo(() => {
    const indices = displayedFilterGroup.clauses.map((_, i) => i);
    return indices.sort((a, b) => {
      const aIsGroup = isFilterGroup(displayedFilterGroup.clauses[a]);
      const bIsGroup = isFilterGroup(displayedFilterGroup.clauses[b]);
      if (aIsGroup === bIsGroup) {
        return 0;
      }
      return aIsGroup ? 1 : -1;
    });
  }, [displayedFilterGroup.clauses]);

  // Determine if we should show filter row (different logic for filter groups)
  const shouldShowFilterGroupRow =
    enableFilterGroups && displayedFilterGroup.clauses.length > 0;

  const hasActiveFilters = shouldShowFilterRow || shouldShowFilterGroupRow;

  const viewActionButtons = hideViewActionButtons ? null : (
    <>
      {onlySaveView && showUpdateViewButton && (
        <RedoButton
          hierarchy="primary"
          onClick={saveViewHandler}
          text={t("Update view")}
        />
      )}
      {showViewActionButtons && (
        <>
          {showSaveNewViewButton && (
            <RedoButton
              hierarchy="secondary"
              onClick={() => setSaveViewModalOpen(true)}
              text={t("Save as new view")}
            />
          )}
          {showUpdateViewButton && (
            <RedoButtonDropdown
              dropdownOpen={editViewDropdownOpen}
              onClick={saveViewHandler}
              pending={saveViewLoad?.pending}
              ref={setEditViewDropdownRef}
              setDropdownOpen={setEditViewDropdownOpen}
              size="sm"
              text={t("Update view")}
            >
              <RedoCommandMenu
                anchor={editViewDropdownRef}
                items={[editViewItem, duplicateViewItem, deleteViewItem]}
                open={editViewDropdownOpen}
                setOpen={setEditViewDropdownOpen}
              />
            </RedoButtonDropdown>
          )}
          {showEditViewButton && (
            <RedoButtonDropdown
              dropdownOpen={editViewDropdownOpen}
              onClick={() => setViewBeingEdited?.(true)}
              ref={setEditViewDropdownRef}
              setDropdownOpen={setEditViewDropdownOpen}
              size="sm"
              text={t("Edit view")}
            >
              <RedoCommandMenu
                anchor={editViewDropdownRef}
                items={[duplicateViewItem, deleteViewItem]}
                open={editViewDropdownOpen}
                setOpen={setEditViewDropdownOpen}
              />
            </RedoButtonDropdown>
          )}
        </>
      )}
    </>
  );

  const searchField = searchEnabled ? (
    <>
      <RedoTextInput
        IconLeading={SearchIcon}
        IconTrailing={() => (
          <Flex
            className={table2Css.closeIcon}
            flexDirection="column"
            onClick={() => setSearch("")}
          >
            {" "}
            <XCloseIconSvg />{" "}
          </Flex>
        )}
        maxLength={200}
        placeholder={searchPlaceholder ?? t("Search")}
        ref={setSearchRef}
        setValue={setSearch}
        value={search}
      />
      {searchButton}
    </>
  ) : null;

  return (
    <Flex
      align="stretch"
      dir="column"
      gap={cardLayout ? "lg" : "none"}
      pb={filterBarBottomPadding}
    >
      {headerTitle && !inlineHeaderTitle && (
        <Flex dir="column" pt="3xl" px={xPadding}>
          <Text
            fontSize={embeddedTable ? "md" : "lg"}
            fontWeight="semibold"
            textColor="primary"
          >
            {headerTitle}
          </Text>
          {headerSubtitle && (
            <Text fontSize="sm" fontWeight="regular" textColor="secondary">
              {headerSubtitle}
            </Text>
          )}
        </Flex>
      )}

      {primaryFilterOptions && (
        <Flex dir="row">
          <Flex grow={1} pt="xl">
            <Filters
              dynamicFilters={dynamicFilters}
              filter={primaryFilter}
              filterCounts={countLoad.value}
              filters={primaryFilterOptions}
              putTabsInHeader={putTabsInHeader}
              setFilter={setPrimaryFilter}
            />
          </Flex>
        </Flex>
      )}

      <Flex
        align="center"
        dir="row"
        p={inlineHeaderTitle ? "3xl" : undefined}
        pt={inlineHeaderTitle || cardLayout ? undefined : "xl"}
        px={inlineHeaderTitle ? undefined : xPadding}
        w={inlineHeaderTitle ? "full" : undefined}
        wrap={inlineHeaderTitle ? "nowrap" : "wrap"}
      >
        {inlineHeaderTitle && headerTitle ? (
          <Flex align="center" gap="sm" shrink={0}>
            <Text fontSize="md" fontWeight="semibold">
              {headerTitle}
            </Text>
          </Flex>
        ) : null}
        {searchField}
        {shouldShowFilterButton && (
          <>
            <RedoButton
              aria-label={t("Filter table")}
              dataTestId="table-filter-button"
              hierarchy="secondary"
              IconLeading={FilterLinesIcon}
              onClick={() => {
                setFilterDropdownOpen(!filterDropdownOpen);
              }}
              ref={setFiltersDropdownButtonRef}
            />
            <RedoMultiselectDropdown
              dropdownAnchor={filtersDropdownButtonRef}
              dropdownOpen={filterDropdownOpen}
              items={filterComponents.map<RedoListItem<string>>(
                (component) => ({
                  id: component.name,
                  type: "text",
                  text: getFilterDisplayName(component),
                  value: component.name,
                }),
              )}
              selectedItems={selectedFilters.map((filter) => ({
                id: filter,
                type: "text",
                text: getFilterDisplayName(
                  filterComponents.find(
                    (component) => component.name === filter,
                  ) ?? { name: filter },
                ),
                value: filter,
              }))}
              setDropdownOpen={setFilterDropdownOpen}
              setSelectedItems={(items) => {
                setSelectedFilters(items.map((item) => item.value));
              }}
            />
          </>
        )}

        {shouldShowAdvancedFilterButton && (
          <>
            {filterInfo ? (
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <div
                    className={table2Css.filterButtonAnchor}
                    data-web="Table2Header"
                  >
                    <RedoButton
                      aria-label={t("Filter", {
                        context: "button that opens table filters",
                      })}
                      dataTestId="table-filter-button"
                      hierarchy="secondary"
                      IconLeading={FilterLinesIcon}
                      onClick={handleFilterButtonClick}
                      ref={setAdvancedFiltersDropdownButtonRef}
                    />
                    <RedoBadge
                      className="absolute -top-2 -right-2 rounded-full"
                      color="blueLight"
                      iconTrailing={{ type: "icon", Icon: InfoCircleIcon }}
                      shadow
                      size="sm"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-md" side="bottom">
                  {filterInfo}
                </TooltipContent>
              </ShadcnTooltip>
            ) : (
              <RedoButton
                aria-label={t("Filter", {
                  context: "button that opens table filters",
                })}
                dataTestId="table-filter-button"
                hierarchy="secondary"
                IconLeading={FilterLinesIcon}
                onClick={handleFilterButtonClick}
                ref={setAdvancedFiltersDropdownButtonRef}
              />
            )}
            {(!enableFilterGroups && !legacyFilterMenu) || nestedCascade ? (
              <RedoCommandMenu
                anchor={
                  showAllFiltersModal
                    ? modalAddFilterButtonRef
                    : advancedFiltersDropdownButtonRef
                }
                fitToAnchor={false}
                items={cascadeFilterItems}
                open={advancedFiltersDropdownOpen}
                placement={showAllFiltersModal ? "bottom-start" : undefined}
                searchable
                searchPlaceholder={t("Search filters...")}
                setOpen={setAdvancedFiltersDropdownOpen}
              />
            ) : (
              advancedFiltersDropdownOpen && (
                <ClickAwayListener
                  onClickAway={() => {
                    setAdvancedFiltersDropdownOpen(false);
                    setFilterSearchText("");
                    setExpandedSubFilter(null);
                  }}
                >
                  <Dropdown
                    anchor={
                      showAllFiltersModal
                        ? modalAddFilterButtonRef
                        : advancedFiltersDropdownButtonRef
                    }
                    className={
                      showAllFiltersModal
                        ? table2Css.filterDropdownModal
                        : table2Css.filterDropdown
                    }
                    fitToAnchor={false}
                    flexProps={{ w: "full", p: "none" }}
                    open={advancedFiltersDropdownOpen}
                    placement={showAllFiltersModal ? "bottom-start" : undefined}
                  >
                    <Flex flexDirection="column" gap="none">
                      <Flex
                        borderBottomWidth="1px"
                        borderColor="primary"
                        borderStyle="solid"
                        className={table2Css.filterDropdownHeader}
                        flexDirection="column"
                        px="lg"
                        py="md"
                      >
                        <BaseRedoInput
                          autoFocus
                          placeholder={t("Search filters...")}
                          setValue={setFilterSearchText}
                          size="sm"
                          value={filterSearchText}
                        />
                      </Flex>
                      <Flex flexDirection="column" gap="none" px="md" py="sm">
                        {enableFilterGroups && (
                          <>
                            <Flex
                              className={table2Css.filterItem}
                              gap="sm"
                              onClick={handleAddNestedGroup}
                            >
                              <LayersIcon />
                              <Text fontSize="xs" fontWeight="medium">
                                {t("Add filter group")}
                              </Text>
                            </Flex>
                            <div className={table2Css.filterDropdownDivider} />
                          </>
                        )}
                        {expandedSubFilter && (
                          <>
                            <Flex
                              align="center"
                              className={table2Css.filterItem}
                              gap="sm"
                              onClick={() => setExpandedSubFilter(null)}
                            >
                              <RightArrowIcon
                                className={table2Css.filterBackIcon}
                              />
                              <Text fontSize="xs" fontWeight="medium">
                                {expandedSubFilter.displayName}
                              </Text>
                            </Flex>
                            <div className={table2Css.filterDropdownDivider} />
                          </>
                        )}
                        <IterableMap
                          items={
                            expandedSubFilter
                              ? (expandedSubFilter.subFilters ?? [])
                              : searchedFilters
                          }
                          keyFn={(filter) => filter.data.name}
                        >
                          {(filter) => {
                            function onClick() {
                              if (filter.subFilters) {
                                setExpandedSubFilter(filter);
                                setFilterSearchText("");
                                return;
                              }
                              const resolved = expandedSubFilter
                                ? filter
                                : advancedFilters.find(
                                    (f) => f.data.name === filter.data.name,
                                  );
                              if (!resolved) {
                                return;
                              }

                              if (enableFilterGroups) {
                                handleAddFilterToGroup(resolved);
                              } else {
                                const next = [
                                  ...displayedAdvancedFilters,
                                  resolved,
                                ];
                                setDisplayedAdvancedFilters(next);
                                setAdvancedFilterParams(
                                  encodeAdvancedFilters(next),
                                );
                              }
                              setAdvancedFiltersDropdownOpen(false);
                              setFilterSearchText("");
                              setExpandedSubFilter(null);
                            }
                            return (
                              <Flex
                                className={table2Css.filterItem}
                                data-testid={`advanced-filter-${filter.displayName.toLowerCase().replace(/\s+/g, "-")}`}
                                gap="sm"
                                onClick={onClick}
                              >
                                <filter.Icon />
                                <Text fontSize="xs" fontWeight="medium">
                                  {filter.displayName}
                                </Text>
                                {filter.subFilters && (
                                  <Flex grow={1} justify="flex-end">
                                    <RightArrowIcon
                                      className={table2Css.subFilterArrowIcon}
                                    />
                                  </Flex>
                                )}
                              </Flex>
                            );
                          }}
                        </IterableMap>
                      </Flex>
                    </Flex>
                  </Dropdown>
                </ClickAwayListener>
              )
            )}
          </>
        )}
        {filterButtonTrailingContent}

        <Flex
          className={table2Css.toolbarEnd}
          grow={1}
          justify="flex-end"
          style={{ paddingRight: toolbarEndPaddingRight }}
        >
          {headerSideText && (
            <Flex align="center">
              <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
                {headerSideText}
              </Text>
            </Flex>
          )}
          {aboveTableContent}
          {(csv || getCsvColumns || onExportRequested) && (
            <ExportCsv
              activeView={activeView}
              appliedAdvancedFilters={decodeAdvancedFilters(
                advancedFilterParams,
                advancedFilters,
              )}
              appliedFilterGroup={
                enableFilterGroups ? displayedFilterGroup : undefined
              }
              columnWidths={columnWidths}
              csv={csv}
              customReportDropdownItems={customReportDropdownItems}
              displayedColumns={displayedColumns}
              downloadTriggerRef={downloadTriggerRef}
              exportButtonLeftShift={exportButtonLeftShift}
              fetcher={fetcher}
              filename={filename || "Redo"}
              filters={filters}
              getCsvColumns={getCsvColumns}
              hideExportButton={hideExportButton}
              onDownloadStatusChange={onDownloadStatusChange}
              onExportRequested={onExportRequested}
              passThroughValues={passThroughValues}
              primaryFilter={primaryFilter}
              search={search}
              sort={sort}
            />
          )}
          {columnSelection && (
            <Flex>
              <RedoButton
                aria-label={t("Configure visible columns")}
                dataTestId="column-selection-dropdown-button"
                hierarchy="secondary"
                IconLeading={ColumnsIcon}
                ref={setColumnSelectionDropdownButtonRef}
              />
              <ColumnSelector
                columns={columns}
                displayedColumns={displayedColumns}
                dropdownButtonRef={columnSelectionDropdownButtonRef}
                setDisplayedColumns={setDisplayedColumns}
              />
            </Flex>
          )}
          {actionButton}
          {!hasActiveFilters && viewActionButtons}
        </Flex>
      </Flex>
      {(shouldShowFilterRow || shouldShowFilterGroupRow) && (
        <Flex
          dir="row"
          gap="lg"
          grow={1}
          justify="space-between"
          pt={cardLayout ? undefined : "2xl"}
          px={xPadding}
        >
          {/* Filter chips and nested groups (when enableFilterGroups is true) */}
          {enableFilterGroups ? (
            <FilterOverflowContainer
              onOverflowChange={setIsFiltersOverflowing}
              onResetFiltersClick={
                showResetFilters &&
                !filterGroupMatches &&
                activeView?.filterGroup
                  ? () => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      handleFilterGroupChange(activeView.filterGroup!);
                    }
                  : undefined
              }
              onViewAllClick={() => {
                setShowAllFiltersModal(true);
              }}
              showResetFilters={showResetFilters && !filterGroupMatches}
            >
              {sortedClauseIndices.map((index) => {
                const clause = displayedFilterGroup.clauses[index];
                if (!clause) {
                  return null;
                }
                const key = isFilterGroup(clause)
                  ? `group-${index}`
                  : clause.name + index;

                // Nested filter group - show as FilterGroupChip
                if (isFilterGroup(clause)) {
                  return (
                    <FilterGroupChip
                      availableFilters={advancedFilters}
                      filterGroup={clause}
                      key={key}
                      onFilterGroupChange={(newGroup) =>
                        handleNestedGroupChange(index, newGroup)
                      }
                      onRemove={() => handleClauseRemove(index)}
                      renderFilter={renderFilterForGroup}
                    />
                  );
                }

                // Simple filter - show as individual chip
                const matchingFilter = advancedFilters.find(
                  (f) => f.data.name === clause.name,
                );
                if (!matchingFilter) {
                  return null;
                }

                const tableFilter = deepCopyObject(matchingFilter);
                tableFilter.data = clause;

                return (
                  <AdvancedFilterComponent
                    filter={tableFilter}
                    filters={displayedFilterGroup.clauses
                      .filter((c): c is AdvancedFilterData => !isFilterGroup(c))
                      .map((c) => {
                        const f = advancedFilters.find(
                          (af) => af.data.name === c.name,
                        );
                        if (!f) {
                          return null;
                        }
                        const copy = deepCopyObject(f);
                        copy.data = c;
                        return copy;
                      })
                      .filter((f): f is AdvancedTableFilter => f !== null)}
                    isViewFilter
                    key={key}
                    removeFilter={() => handleClauseRemove(index)}
                    setValue={(newFilter) =>
                      handleClauseChange(index, newFilter)
                    }
                  />
                );
              })}
            </FilterOverflowContainer>
          ) : (
            /* Legacy flat filter list */
            <FilterOverflowContainer
              onOverflowChange={setIsFiltersOverflowing}
              onResetFiltersClick={
                shouldShowAdvancedFilterButton && !allFiltersMatch
                  ? () => {
                      setAdvancedFilterParams(
                        getAdvancedFilterArray(activeView, advancedFilters),
                      );
                    }
                  : undefined
              }
              onViewAllClick={() => {
                setShowAllFiltersModal(true);
              }}
              showResetFilters={
                showResetFilters &&
                shouldShowAdvancedFilterButton &&
                !allFiltersMatch
              }
            >
              {shouldShowFilterRow && shouldShowFilterButton && (
                <FilterChipsList
                  filterComponents={filterComponents}
                  filters={filters}
                  onFiltersChange={setFilters}
                  onSelectedFiltersChange={setSelectedFilters}
                  selectedFilters={selectedFilters}
                />
              )}
              {shouldShowFilterRow && shouldShowAdvancedFilterButton && (
                <IterableMap
                  items={displayedAdvancedFilters}
                  keyFn={(filter, index) => filter.data.name + index}
                >
                  {(filter, index) => {
                    function setValue(newFilter: AdvancedTableFilter) {
                      const newFilters = displayedAdvancedFilters.map(
                        (f, i) => {
                          if (index === i) {
                            return newFilter;
                          }
                          return f;
                        },
                      );
                      setAdvancedFilterParams(
                        encodeAdvancedFilters(newFilters),
                      );
                    }

                    function removeFilter() {
                      const newFilters = displayedAdvancedFilters.filter(
                        (_, i) => i !== index,
                      );
                      setAdvancedFilterParams(
                        encodeAdvancedFilters(newFilters),
                      );
                    }
                    return (
                      <AdvancedFilterComponent
                        filter={filter}
                        filters={displayedAdvancedFilters}
                        isViewFilter
                        removeFilter={removeFilter}
                        setValue={setValue}
                      />
                    );
                  }}
                </IterableMap>
              )}
            </FilterOverflowContainer>
          )}
          <Flex className={table2Css.viewButtonContainer} justify="flex-end">
            {viewActionButtons}
          </Flex>
        </Flex>
      )}
      {showViewActionButtons && (
        <>
          {deleteViewModalOpen && (
            <DeleteTableViewModal
              onDelete={() => {
                onDeleteView(activeView);
                setDeleteViewModalOpen(false);
              }}
              open={deleteViewModalOpen}
              setOpen={(open) => {
                setDeleteViewModalOpen(open);
              }}
              view={activeView}
            />
          )}
          {saveViewModalOpen && (
            <SaveTableViewModal
              cancel={() => setSaveViewModalOpen(false)}
              reservedViewNames={reservedViewNames || new Set()}
              save={async (name) => {
                saveView({
                  name,
                  filters: appliedAdvancedFilters.map((filter) => filter.data),
                  filterGroup: enableFilterGroups
                    ? displayedFilterGroup
                    : undefined,
                  columns: getColumnsFromDisplayedColumns(
                    columns,
                    displayedColumns,
                  ).map((col) => ({
                    key: col.key,
                    width: columnWidths?.[col.key] ?? col.width,
                    hidden: col?.hidden ?? false,
                    pinned: columnPins?.[col.key] ?? false,
                  })),
                  sort,
                  pageSize,
                  position: activeView.position,
                  showNotification:
                    viewNotificationsEnabled && "showNotification" in activeView
                      ? activeView.showNotification
                      : undefined,
                  hideWhenEmpty:
                    viewNotificationsEnabled && "hideWhenEmpty" in activeView
                      ? activeView.hideWhenEmpty
                      : undefined,
                });
                setSaveViewModalOpen(false);
              }}
            />
          )}
        </>
      )}
      {hasActiveFilters &&
        (enableFilterGroups && displayedFilterGroup.clauses.length > 0 ? (
          <AllFiltersModal
            addFilterButton={
              <RedoButton
                hierarchy="primary"
                IconLeading={FilterLinesIcon}
                onClick={() => {
                  setAdvancedFiltersDropdownOpen(true);
                }}
                ref={setModalAddFilterButtonRef}
                text={t("Add filter")}
              />
            }
            onClose={() => setShowAllFiltersModal(false)}
            onResetFilters={
              showResetFilters && !filterGroupMatches && activeView?.filterGroup
                ? () => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    handleFilterGroupChange(activeView.filterGroup!);
                  }
                : undefined
            }
            open={showAllFiltersModal}
          >
            {sortedClauseIndices.map((index) => {
              const clause = displayedFilterGroup.clauses[index];
              if (!clause) {
                return null;
              }
              const key = isFilterGroup(clause)
                ? `group-${index}`
                : clause.name + index;

              if (isFilterGroup(clause)) {
                return (
                  <FilterGroupChip
                    availableFilters={advancedFilters}
                    filterGroup={clause}
                    key={key}
                    onFilterGroupChange={(newGroup) =>
                      handleNestedGroupChange(index, newGroup)
                    }
                    onRemove={() => handleClauseRemove(index)}
                    renderFilter={renderFilterForGroup}
                  />
                );
              }

              const matchingFilter = advancedFilters.find(
                (f) => f.data.name === clause.name,
              );
              if (!matchingFilter) {
                return null;
              }

              const tableFilter = deepCopyObject(matchingFilter);
              tableFilter.data = clause;

              return (
                <AdvancedFilterComponent
                  filter={tableFilter}
                  filters={(() => {
                    const filterLookup = new Map(
                      advancedFilters.map((f) => [f.data.name, f]),
                    );
                    return displayedFilterGroup.clauses
                      .filter((c): c is AdvancedFilterData => !isFilterGroup(c))
                      .map((c) => {
                        const f = filterLookup.get(c.name);
                        if (!f) {
                          return null;
                        }
                        const copy = deepCopyObject(f);
                        copy.data = c;
                        return copy;
                      })
                      .filter((f): f is AdvancedTableFilter => f !== null);
                  })()}
                  isViewFilter
                  key={key}
                  removeFilter={() => handleClauseRemove(index)}
                  setValue={(newFilter) => handleClauseChange(index, newFilter)}
                />
              );
            })}
          </AllFiltersModal>
        ) : (
          <TableFiltersModal
            addFilterButtonRef={setModalAddFilterButtonRef}
            AdvancedFilterComponent={AdvancedFilterComponent}
            allFiltersMatch={allFiltersMatch}
            displayedAdvancedFilters={displayedAdvancedFilters}
            filterComponents={filterComponents}
            filters={filters}
            onAddFilterClick={() => {
              setAdvancedFiltersDropdownOpen(true);
            }}
            onAdvancedFiltersChange={setAdvancedFilterParams}
            onClose={() => setShowAllFiltersModal(false)}
            onFiltersChange={setFilters}
            onResetToViewFilters={() => {
              setAdvancedFilterParams(
                getAdvancedFilterArray(activeView, advancedFilters),
              );
            }}
            onSelectedFiltersChange={setSelectedFilters}
            open={showAllFiltersModal}
            selectedFilters={selectedFilters}
            shouldShowAdvancedFilterButton={shouldShowAdvancedFilterButton}
            shouldShowFilterButton={shouldShowFilterButton}
            showResetFilters={showResetFilters}
          />
        ))}
    </Flex>
  );
});

function getDisplayedColumns<T>({
  activeView,
  columnOptions,
}: {
  activeView: viewTypes[keyof viewTypes] | undefined;
  columnOptions: Column2<T>[];
}): DisplayedColumn[] {
  if (!activeView?.columns) {
    return columnOptions.map((column) => ({
      key: column.key,
      displayed: !column.hidden,
    }));
  }

  const displayedCols: DisplayedColumn[] = [];
  for (const viewColumn of activeView.columns) {
    displayedCols.push({ key: viewColumn.key, displayed: !viewColumn.hidden });
  }

  for (const col of columnOptions) {
    if (!displayedCols.some((displayedCol) => displayedCol.key === col.key)) {
      displayedCols.push({ key: col.key, displayed: false });
    }
  }

  return displayedCols;
}

function getActiveViewStorageKey(
  activeView: viewTypes[keyof viewTypes] | undefined,
): string | null {
  return activeView?._id ?? activeView?.name ?? null;
}

function getStoredDisplayedColumns(
  columnSelectionStorageKey: string | undefined,
): DisplayedColumn[] | null {
  if (!columnSelectionStorageKey) {
    return null;
  }

  try {
    const stored = localStorage.getItem(columnSelectionStorageKey);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return stored ? (JSON.parse(stored) as DisplayedColumn[]) : null;
  } catch (e) {
    console.error("Failed to load column selection from localStorage:", e);
    return null;
  }
}

function getColumnWidths<T>({
  activeView,
  columnOptions,
}: {
  activeView: viewTypes[keyof viewTypes] | undefined;
  columnOptions: Column2<T>[];
}): Record<string, number> {
  return columnOptions.reduce((acc: Record<string, number>, col) => {
    const viewCol = activeView?.columns?.find(
      (viewCol) => viewCol.key === col.key,
    );
    if (viewCol) {
      acc[col.key] = viewCol.width;
    } else {
      acc[col.key] = col.width;
    }
    return acc;
  }, {});
}

function getColumnPins<T>({
  activeView,
  columnOptions,
}: {
  activeView: viewTypes[keyof viewTypes] | undefined;
  columnOptions: Column2<T>[];
}): Record<string, boolean> {
  return columnOptions.reduce((acc: Record<string, boolean>, col) => {
    const viewCol = activeView?.columns?.find(
      (viewCol) => viewCol.key === col.key,
    );
    if (viewCol?.pinned !== undefined) {
      acc[col.key] = viewCol.pinned;
    }
    return acc;
  }, {});
}

// Sentinel sort key: no column shows an arrow, fetcher gets undefined (relevance).
const RELEVANCE_SORT_KEY = "__relevance__";

function getViewSort(
  activeView: viewTypes[keyof viewTypes] | undefined,
): TableSort | null {
  return activeView?.sort
    ? { key: activeView.sort.key, direction: activeView.sort.direction }
    : null;
}

export const Table2 = genericMemo(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  forwardRef(TableComponent) as <T>(
    props: TableProps2<T> & { ref?: React.ForwardedRef<TableRef<T>> },
  ) => ReturnType<typeof TableComponent>,
);

function TableComponent<T>(
  props: TableProps2<T>,
  ref: ForwardedRef<TableRef<T>>,
) {
  const {
    adjustableColumnWidths,
    allowPinning,
    downloadTriggerRef,
    onRowClick,
    onRowHovered,
    isRowSelected,
    isRowClickable,
    rowPath,
    primaryFilterDefault,
    sortDefault,
    fetcher,
    filterComponents = [],
    columns,
    searchEnabled = false,
    searchButton,
    relevanceSort = false,
    passThroughValues = null,
    paramPrefix,
    alwaysLoadTable = false,
    onQuickDownload,
    pageNumber,
    setPageNumber,
    title,
    subtitle,
    scrollable = false,
    theme = TableTheme.COMPACT,
    scrollAreaRef,
    header,
    externalSetSort,
    externalSort,
    pageSize,
    setPageSize,
    validPageSizes,
    EmptyContentMessage,
    LoadingContentMessage,
    onContextMenu,
    size,
    stickyPageControl,
    hidePageControl,
    advancedFilters,
    initialAdvancedFilters,
    refreshSymbol,
    viewBeingEdited,
    setViewBeingEdited,
    activeView,
    focusSearchOnRender,
    saveView,
    reservedViewNames,
    onDeleteView,
    onlySaveView,
    saveViewLoad,
    tableContentClassName,
    hideHeader,
    putTabsInHeader,
    useVirtualTable,
    maxAvailableHeight,
    fillContainer,
    surfaceData,
    enableColumnReordering = false,
    expander,
    expanderPosition = "first",
    totalCountTooltip,
    aboveTableContent,
    filterButtonTrailingContent,
    onExportRequested,
    exportButtonLeftShift,
    enableFilterGroups,
    searchDebounceMs = 200,
    showResetFilters = true,
    cardLayout = false,
    embeddedTable,
  } = props;
  const [filters, setFilters] = useParam<{ [name: string]: string }>(
    new ObjectParam(
      Object.fromEntries(
        filterComponents.map((component) => [component.name, component.param]),
      ),
    ),
    objectEqual(
      Object.fromEntries(
        filterComponents.map((component) => [component.name, stringEqual]),
      ),
    ),
  );
  const [primaryFilter, setPrimaryFilter] = useParam(
    Filter.param(primaryFilterDefault || ""),
  );
  const [search, setSearch] = useParam(Search.param(paramPrefix));
  const [internalSort, internalSetSort] = useParam(
    sortParam(getViewSort(activeView) ?? sortDefault, paramPrefix),
    sortEqual,
  );

  // Callers rebuild the activeView object on unrelated renders, so resetting on
  // object identity would overwrite the sort the user just clicked.
  const activeViewKey = getActiveViewStorageKey(activeView);
  const previousActiveViewKey = useRef(activeViewKey);
  const activeViewChanged = previousActiveViewKey.current !== activeViewKey;
  useEffect(() => {
    if (previousActiveViewKey.current === activeViewKey) {
      return;
    }
    previousActiveViewKey.current = activeViewKey;
    const newSort = getViewSort(activeView) ?? sortDefault;
    if (!sortEqual(internalSort, newSort)) {
      internalSetSort(newSort);
    }
    const activeViewPageSize = activeView?.pageSize;
    if (activeViewPageSize != null && activeViewPageSize !== pageSize) {
      setPageSize?.(activeViewPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewKey]);

  const [advancedFilterParams, setAdvancedFilterParams] = useParam(
    new StringParam(
      paramPrefix ? `${paramPrefix}AdvancedFilters` : "advancedFilters",
      activeView
        ? getAdvancedFilterArray(activeView, advancedFilters)
        : initialAdvancedFilters
          ? encodeAdvancedFilters(initialAdvancedFilters)
          : "",
    ),
  );

  // Filter group URL parameter (for enableFilterGroups mode)
  const [filterGroupParams, setFilterGroupParams] = useParam(
    new StringParam(
      paramPrefix ? `${paramPrefix}FilterGroup` : "filterGroup",
      activeView?.filterGroup
        ? encodeAdvancedFilterGroup(activeView.filterGroup)
        : "",
    ),
  );

  // Filter group state (for enableFilterGroups mode)
  const [displayedFilterGroup, setDisplayedFilterGroup] = useState(() => {
    if (enableFilterGroups && filterGroupParams) {
      return (
        decodeAdvancedFilterGroup(filterGroupParams, advancedFilters || []) ||
        createEmptyFilterGroup()
      );
    }
    return createEmptyFilterGroup();
  });

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (!activeViewChanged) {
        return; // Keeps filters on refresh
      }
    }

    setAdvancedFilterParams(
      getAdvancedFilterArray(activeView, advancedFilters),
    );

    if (enableFilterGroups) {
      const nextFilterGroup =
        activeView?.filterGroup ?? createEmptyFilterGroup();
      setFilterGroupParams(
        activeView?.filterGroup
          ? encodeAdvancedFilterGroup(activeView.filterGroup)
          : "",
      );
      setDisplayedFilterGroup(nextFilterGroup);
    }
    // Derived table state follows view identity, not fetched object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewKey]);

  const sort = useMemo(
    () => externalSort || internalSort,
    [externalSort, internalSort],
  );
  const setSort = useMemo(
    () => externalSetSort || internalSetSort,
    [externalSetSort, internalSetSort],
  );

  const columnOptions = useRef(columns);
  const displayedColumnsActiveViewSync =
    useRef<DisplayedColumnsActiveViewSyncState>({
      activeViewStorageKey: getActiveViewStorageKey(activeView),
      initializedFromStorage: false,
    });

  const [displayedColumns, setDisplayedColumns] = useState<DisplayedColumn[]>(
    () => {
      const stored = getStoredDisplayedColumns(props.columnSelectionStorageKey);
      if (stored) {
        displayedColumnsActiveViewSync.current.initializedFromStorage = true;
        return stored;
      }

      return getDisplayedColumns({
        activeView,
        columnOptions: columnOptions.current,
      });
    },
  );

  useEffect(() => {
    if (
      shouldSkipDisplayedColumnsActiveViewSync(
        displayedColumnsActiveViewSync.current,
        getActiveViewStorageKey(activeView),
      )
    ) {
      return;
    }

    setDisplayedColumns(
      getDisplayedColumns({ activeView, columnOptions: columnOptions.current }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewKey]);

  // displayedColumns is seeded once and columnsToRender only renders keys in
  // it, so keys added to `columns` after mount must be reconciled in here.
  useEffect(() => {
    columnOptions.current = columns;
    setDisplayedColumns((prev) =>
      reconcileDisplayedColumns(prev, columns, (col) => ({
        key: col.key,
        displayed: !col.hidden,
      })),
    );
  }, [columns]);

  useEffect(() => {
    if (props.columnSelectionStorageKey) {
      try {
        localStorage.setItem(
          props.columnSelectionStorageKey,
          JSON.stringify(displayedColumns),
        );
      } catch (e) {
        console.error("Failed to save column selection to localStorage:", e);
      }
    }
  }, [displayedColumns, props.columnSelectionStorageKey]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    getColumnWidths({ activeView, columnOptions: columnOptions.current }),
  );
  const [columnPins, setColumnPins] = useState<Record<string, boolean>>(
    getColumnPins({ activeView, columnOptions: columnOptions.current }),
  );
  useEffect(() => {
    setColumnWidths(
      getColumnWidths({ activeView, columnOptions: columnOptions.current }),
    );
    setColumnPins(
      getColumnPins({ activeView, columnOptions: columnOptions.current }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewKey]);

  const pendingActiveViewState = useMemo(() => {
    if (!activeViewChanged) {
      return null;
    }

    return {
      advancedFilterParams: getAdvancedFilterArray(activeView, advancedFilters),
      columnPins: getColumnPins({ activeView, columnOptions: columns }),
      columnWidths: getColumnWidths({ activeView, columnOptions: columns }),
      displayedColumns: getDisplayedColumns({
        activeView,
        columnOptions: columns,
      }),
      displayedFilterGroup: activeView?.filterGroup ?? createEmptyFilterGroup(),
      filterGroupParams: activeView?.filterGroup
        ? encodeAdvancedFilterGroup(activeView.filterGroup)
        : "",
      pageSize: activeView?.pageSize ?? pageSize,
      sort: externalSort ?? getViewSort(activeView) ?? sortDefault,
    };
  }, [
    activeView,
    activeViewChanged,
    advancedFilters,
    columns,
    externalSort,
    pageSize,
    sortDefault,
  ]);

  const renderedViewState = {
    advancedFilterParams:
      pendingActiveViewState?.advancedFilterParams ?? advancedFilterParams,
    columnPins: pendingActiveViewState?.columnPins ?? columnPins,
    columnWidths: pendingActiveViewState?.columnWidths ?? columnWidths,
    displayedColumns:
      pendingActiveViewState?.displayedColumns ?? displayedColumns,
    displayedFilterGroup:
      pendingActiveViewState?.displayedFilterGroup ?? displayedFilterGroup,
    filterGroupParams:
      pendingActiveViewState?.filterGroupParams ?? filterGroupParams,
    pageSize: pendingActiveViewState?.pageSize ?? pageSize,
    sort: pendingActiveViewState?.sort ?? sort,
  };

  const debouncedSearch = useDebounce(search, searchDebounceMs);

  // Reset to page 0 when the search term changes; the ref-compare skips mount so a
  // deep-linked page isn't clobbered.
  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setPageNumber?.(0);
    }
  }, [debouncedSearch, setPageNumber]);

  // A search defaults to relevance; a column picked while searching is scoped to
  // that exact term, so changing/clearing the search reverts to relevance.
  const searching = relevanceSort && searchEnabled && !!debouncedSearch?.trim();
  const [searchSort, setSearchSort] = useState<{
    term: string;
    sort: TableSort;
  } | null>(null);
  const activeSearchSort =
    searchSort?.term === debouncedSearch ? searchSort.sort : null;
  const relevanceActive = searching && activeSearchSort === null;
  const effectiveSort = searching
    ? (activeSearchSort ?? renderedViewState.sort)
    : renderedViewState.sort;
  const effectiveSetSort = useCallback(
    (next: TableSort) => {
      if (searching) {
        setSearchSort({ term: debouncedSearch ?? "", sort: next });
      } else {
        setSort(next);
      }
    },
    [searching, debouncedSearch, setSort],
  );

  const advancedFiltersValue = useMemo(
    () =>
      decodeAdvancedFilters(
        renderedViewState.advancedFilterParams,
        advancedFilters || [],
      ),
    [renderedViewState.advancedFilterParams, advancedFilters],
  );
  const appliedAdvancedFilters = useViewScopedDebounce(
    advancedFiltersValue,
    500,
    activeViewKey,
  );

  // Debounce filter group for API calls (only when enableFilterGroups is true)
  const appliedFilterGroup = useViewScopedDebounce(
    enableFilterGroups ? renderedViewState.displayedFilterGroup : undefined,
    500,
    activeViewKey,
  );

  const countLoad = useLoad(
    async (signal) => {
      const counts = await fetcher.counts(
        filters,
        debouncedSearch || undefined,
        signal,
        appliedAdvancedFilters,
        appliedFilterGroup,
      );

      const numPages = counts.total
        ? Math.ceil(
            counts.total / (renderedViewState.pageSize || DEFAULT_PAGE_SIZE),
          )
        : 0;

      if (
        setPageNumber &&
        pageNumber !== undefined &&
        pageNumber > 0 &&
        pageNumber > numPages - 1
      ) {
        setPageNumber(0);
      }
      return counts;
    },
    [
      fetcher,
      debouncedSearch,
      filters,
      appliedAdvancedFilters,
      appliedFilterGroup,
      renderedViewState.pageSize,
    ],
  );

  const headerPortal = useContext(HeaderOverrideContext);

  useEffect(() => {
    if (viewBeingEdited && activeView && saveView && reservedViewNames) {
      headerPortal(
        <TableFilterEditorHeader
          activeView={activeView}
          cancel={() => setViewBeingEdited?.(false)}
          reservedViewNames={reservedViewNames}
          save={async (body) => {
            saveView({
              name: body.name,
              filters: appliedAdvancedFilters.map((filter) => filter.data),
              filterGroup: enableFilterGroups
                ? displayedFilterGroup
                : undefined,
              columns: getColumnsFromDisplayedColumns(
                columns,
                displayedColumns,
              ).map((col) => ({
                key: col.key,
                width: columnWidths?.[col.key] ?? col.width,
                hidden: col?.hidden ?? false,
                pinned: columnPins?.[col.key] ?? false,
              })),
              sort,
              pageSize,
              _id: activeView._id,
              position: activeView.position,
              showNotification: body.showNotification,
              hideWhenEmpty: body.hideWhenEmpty,
            });
          }}
          savePending={saveViewLoad?.pending ?? false}
          viewNotificationsEnabled={props.viewNotificationsEnabled}
        />,
      );
    } else {
      headerPortal(null);
    }
    return () => headerPortal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnWidths,
    headerPortal,
    viewBeingEdited,
    filters,
    saveView,
    activeView,
    reservedViewNames,
    appliedAdvancedFilters,
    displayedColumns,
    displayedFilterGroup,
    sort,
    setViewBeingEdited,
    saveViewLoad?.pending,
    pageSize,
  ]);

  const footerRef = useRef<HTMLDivElement>(null);

  const showPageControl =
    !hidePageControl && pageNumber !== undefined && setPageNumber;
  const pageControlPending = activeViewChanged || countLoad.pending;
  const totalCount = countLoad.value?.total;
  const hasVisiblePageControl = Boolean(showPageControl && totalCount);
  const pageControlElement = showPageControl ? (
    <PageControl
      cardLayout={cardLayout}
      hasNext={
        totalCount !== undefined
          ? totalCount >
            (pageNumber + 1) * (renderedViewState.pageSize || DEFAULT_PAGE_SIZE)
          : false
      }
      numPages={
        totalCount !== undefined
          ? Math.ceil(
              totalCount / (renderedViewState.pageSize || DEFAULT_PAGE_SIZE),
            )
          : 0
      }
      pageNumber={pageNumber}
      pageSize={renderedViewState.pageSize}
      pending={pageControlPending}
      ref={footerRef}
      setPageNumber={setPageNumber}
      setPageSize={setPageSize}
      stickyPageControl={stickyPageControl}
      totalCountTooltip={totalCountTooltip}
      totalItemCount={countLoad.value?.total || 0}
      validPageSizes={validPageSizes}
    />
  ) : null;

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>(
    () => {
      if (props.expandAllStorageKey) {
        try {
          if (localStorage.getItem(props.expandAllStorageKey) === "true") {
            const initial: Record<number, boolean> = {};
            for (let i = 0; i < (pageSize || DEFAULT_PAGE_SIZE); ++i) {
              initial[i] = true;
            }
            return initial;
          }
        } catch (e) {
          console.error(
            "Failed to load expand-all preference from localStorage:",
            e,
          );
        }
      }
      return {};
    },
  );

  const toggleExpansion = useHandler((rowIndex: number) => {
    setExpandedRows((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  });

  const setAllExpanded = useHandler((expanded: boolean) => {
    setExpandedRows((prev) => {
      const newExpandedRows = { ...prev };
      for (let i = 0; i < (pageSize || DEFAULT_PAGE_SIZE); ++i) {
        newExpandedRows[i] = expanded;
      }
      return newExpandedRows;
    });
    if (props.expandAllStorageKey) {
      try {
        localStorage.setItem(props.expandAllStorageKey, String(expanded));
      } catch (e) {
        console.error(
          "Failed to save expand-all preference to localStorage:",
          e,
        );
      }
    }
  });

  const columnsToRender = useMemo(() => {
    // Filter out unavailable columns first (available defaults to true)
    const availableColumns = columns.filter((col) => col.available !== false);

    const renderCols = getColumnsFromDisplayedColumns(
      availableColumns,
      renderedViewState.displayedColumns,
    ).filter((col) => !col.hidden);

    if (expander) {
      const expanderCol = getExpanderColumn(
        expander,
        expandedRows,
        toggleExpansion,
        setAllExpanded,
      );
      if (expanderPosition === "after-selection") {
        const selectedIdx = renderCols.findIndex((c) => c.key === "selected");
        renderCols.splice(
          selectedIdx >= 0 ? selectedIdx + 1 : 0,
          0,
          expanderCol,
        );
      } else {
        renderCols.unshift(expanderCol);
      }
    }

    return renderCols;
  }, [
    columns,
    renderedViewState.displayedColumns,
    expanderPosition,
    expander,
    expandedRows,
    toggleExpansion,
    setAllExpanded,
  ]);

  return (
    <section className={tableCss.tableContainer} data-web="Table2">
      {!hideHeader && (
        <Table2Header
          {...props}
          aboveTableContent={aboveTableContent}
          activeView={activeView}
          advancedFilterParams={renderedViewState.advancedFilterParams}
          advancedFilters={advancedFilters || []}
          appliedAdvancedFilters={appliedAdvancedFilters}
          columnPins={renderedViewState.columnPins}
          columnWidths={renderedViewState.columnWidths}
          countLoad={countLoad}
          displayedColumns={renderedViewState.displayedColumns}
          displayedFilterGroup={renderedViewState.displayedFilterGroup}
          downloadTriggerRef={downloadTriggerRef}
          exportButtonLeftShift={exportButtonLeftShift}
          filterButtonTrailingContent={filterButtonTrailingContent}
          filterComponents={filterComponents}
          filterGroupParams={renderedViewState.filterGroupParams}
          filters={filters}
          focusSearchOnRender={focusSearchOnRender}
          getCsvColumns={props.getCsvColumns}
          key={activeViewKey}
          onDeleteView={onDeleteView}
          onExportRequested={onExportRequested}
          onlySaveView={onlySaveView}
          pageSize={renderedViewState.pageSize}
          primaryFilter={primaryFilter}
          putTabsInHeader={putTabsInHeader}
          reservedViewNames={reservedViewNames}
          search={search}
          searchButton={searchButton}
          searchEnabled={searchEnabled}
          searchRefreshSymbol={props.searchRefreshSymbol}
          setAdvancedFilterParams={setAdvancedFilterParams}
          setDisplayedColumns={setDisplayedColumns}
          setDisplayedFilterGroup={setDisplayedFilterGroup}
          setFilterGroupParams={setFilterGroupParams}
          setFilters={setFilters}
          setPrimaryFilter={setPrimaryFilter}
          setSearch={setSearch}
          setSort={setSort}
          setViewBeingEdited={setViewBeingEdited}
          showResetFilters={showResetFilters}
          sort={renderedViewState.sort}
          viewBeingEdited={viewBeingEdited}
        />
      )}
      <div
        className={classnames(
          table2Css.tableContentWrapper,
          cardLayout && table2Css.cardLayout,
          cardLayout && hasVisiblePageControl && table2Css.cardLayoutWithFooter,
          cardLayout && !showPageControl && table2Css.cardLayoutNoFooter,
          embeddedTable && redoTableHeaderCellsCss.noBorder,
        )}
      >
        {header && <div className={tableCss.textHeader}>{header}</div>}
        {title && typeof title === "string" && (
          <h3 className={tableCss.title}>{title}</h3>
        )}
        {subtitle && typeof subtitle === "string" && (
          <Text mb="2xl">{subtitle}</Text>
        )}
        <TableContent
          adjustableColumnWidths={adjustableColumnWidths}
          allowPinning={allowPinning}
          alwaysLoadTable={alwaysLoadTable}
          appliedAdvancedFilters={appliedAdvancedFilters}
          appliedFilterGroup={appliedFilterGroup}
          cardLayout={cardLayout}
          className={tableContentClassName}
          columnPins={renderedViewState.columnPins}
          columns={columnsToRender}
          columnWidths={renderedViewState.columnWidths}
          dataScopeKey={activeViewKey}
          displayedColumns={renderedViewState.displayedColumns}
          EmptyContentMessage={EmptyContentMessage}
          enableColumnReordering={enableColumnReordering}
          expandedRows={expandedRows}
          expander={expander}
          fetcher={fetcher}
          fillContainer={fillContainer}
          filters={filters}
          footerRef={footerRef}
          header={header}
          isRowClickable={isRowClickable}
          isRowSelected={isRowSelected}
          LoadingContentMessage={LoadingContentMessage}
          maxAvailableHeight={maxAvailableHeight}
          onContextMenu={onContextMenu}
          onQuickDownload={onQuickDownload}
          onRowClick={onRowClick}
          onRowHovered={onRowHovered}
          pageNumber={pageNumber !== undefined ? pageNumber : undefined}
          pageSize={renderedViewState.pageSize}
          passThroughValues={passThroughValues}
          primaryFilter={primaryFilter}
          ref={ref}
          refreshSymbol={refreshSymbol}
          relevanceActive={relevanceActive}
          relevanceSort={relevanceSort}
          rowPath={rowPath}
          scrollable={scrollable}
          scrollAreaRef={scrollAreaRef}
          search={debouncedSearch}
          setColumnPins={setColumnPins}
          setColumnWidths={setColumnWidths}
          setDisplayedColumns={setDisplayedColumns}
          setSort={effectiveSetSort}
          size={size}
          sort={effectiveSort}
          surfaceData={surfaceData}
          theme={theme}
          toggleExpandedRow={toggleExpansion}
          useVirtualTable={useVirtualTable}
        />
      </div>
      {pageControlElement}
    </section>
  );
}

const PageButton = memo(function PageButton({
  index,
  currentPage,
  setPage,
  isEllipses = false,
}: {
  index: number;
  currentPage: number;
  setPage: (index: number) => void;
  isEllipses?: boolean;
}) {
  return (
    <RedoButton
      hierarchy="tertiary"
      onClick={() => setPage(index)}
      pressed={index === currentPage}
      size="xs"
      text={isEllipses ? "..." : String(index + 1)}
    />
  );
});

export const PageControl = memo(
  forwardRef(function PageControl(
    {
      pageNumber,
      numPages,
      setPageNumber,
      pageSize,
      setPageSize,
      pending,
      validPageSizes,
      hasNext,
      stickyPageControl,
      cardLayout,
      totalItemCount,
      totalCountTooltip,
    }: {
      pageNumber: number;
      numPages: number;
      setPageNumber: (pageNumber: number) => void;
      pageSize: number | undefined;
      hasNext: boolean;
      setPageSize?: (size: number) => void;
      validPageSizes?: ValidPageSize[];
      stickyPageControl?: boolean;
      cardLayout?: boolean;
      totalItemCount: number;
      totalCountTooltip?: { text: string; threshold: number };
      pending: boolean;
    },
    ref: React.Ref<HTMLDivElement>,
  ) {
    const { t } = useTranslation();
    const renderPageNumbers = (
      numPages: number,
      page: number,
      setPage: any,
    ) => {
      const pageNumbers = [];
      for (let i = 0; i < numPages; i++) {
        if (page < 3 && i < 4) {
          pageNumbers.push(
            <PageButton
              currentPage={page}
              index={i}
              key={i}
              setPage={setPage}
            />,
          );
        } else if (page > numPages - 3 && i > numPages - 5) {
          pageNumbers.push(
            <PageButton
              currentPage={page}
              index={i}
              key={i}
              setPage={setPage}
            />,
          );
        }
        // Last page/first page
        else if (i === numPages - 1 || i === 0) {
          pageNumbers.push(
            <PageButton
              currentPage={page}
              index={i}
              key={i}
              setPage={setPage}
            />,
          );
        }
        // Current page/adjacent pages
        else if (i === page - 1 || i === page + 1 || i === page) {
          pageNumbers.push(
            <PageButton
              currentPage={page}
              index={i}
              key={i}
              setPage={setPage}
            />,
          );
        }
      }

      if (numPages > 5) {
        if (page > 2) {
          const middleIndex = Math.floor(page / 2);
          pageNumbers.splice(
            1,
            0,
            <PageButton
              currentPage={page}
              index={middleIndex}
              isEllipses
              key={middleIndex}
              setPage={setPage}
            />,
          );
        }

        if (page < numPages - 3) {
          const offset = page > 2 ? 1 : 3;
          const middleIndex = Math.floor((numPages + page + offset) / 2);
          pageNumbers.splice(
            -1,
            0,
            <PageButton
              currentPage={page}
              index={middleIndex}
              isEllipses
              key={middleIndex}
              setPage={setPage}
            />,
          );
        }
      }
      return pageNumbers;
    };

    const [pageSizeRef, setPageSizeRef] = useState<HTMLButtonElement | null>(
      null,
    );

    const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);

    const pageSizes = validPageSizes || VALID_PAGE_SIZES;

    const pageSizeOptions: RedoListItem<number>[] = pageSizes.map((size) => ({
      id: size,
      value: size,
      type: "text" as const,
      text: size.toString(),
    }));

    const hasItems = totalItemCount > 0;
    const hasFooterFootprint = hasItems || pending;
    const beginningItemCount = pageNumber * (pageSize || DEFAULT_PAGE_SIZE) + 1;
    const endingItemCount = hasNext
      ? (pageNumber + 1) * (pageSize || DEFAULT_PAGE_SIZE)
      : totalItemCount;
    const pageControlStyle: React.CSSProperties | undefined = pending
      ? { visibility: "hidden" }
      : undefined;

    return (
      <Flex
        borderWidth={hasFooterFootprint ? undefined : "none"}
        className={classnames(
          tableCss.pageControl,
          stickyPageControl ? tableCss.pageControlSticky : "",
          cardLayout ? table2Css.cardLayoutPageControl : "",
        )}
        justify="space-between"
        overflow={hasFooterFootprint ? undefined : "hidden"}
        p={hasFooterFootprint ? undefined : "none"}
        ref={ref}
        style={pageControlStyle}
        wrap="wrap"
      >
        {hasFooterFootprint && (
          <>
            <Flex align="center" flex="1" gap="md" justify="flex-start">
              <Text fontSize="xs" textColor="secondary">
                {t("Showing {{start}}-{{end}} of {{total}}", {
                  start: beginningItemCount,
                  end: endingItemCount,
                  total: totalItemCount,
                })}
              </Text>
              {totalCountTooltip &&
                totalItemCount >= totalCountTooltip.threshold && (
                  <Tooltip title={totalCountTooltip.text}>
                    <div className={tableCss.infoIcon}>
                      <InfoCircleIcon />
                    </div>
                  </Tooltip>
                )}
            </Flex>

            <Flex align="center" flex="1" gap="xs" justify="center">
              <RedoButton
                aria-label={t("Previous page")}
                disabled={pageNumber === 0}
                hierarchy="tertiary"
                IconLeading={ChevronLeftIcon}
                onClick={() => setPageNumber(pageNumber - 1)}
                size="xs"
              />
              {renderPageNumbers(numPages, pageNumber, setPageNumber)}
              <RedoButton
                aria-label={t("Next page")}
                disabled={!hasNext}
                hierarchy="tertiary"
                IconLeading={ChevronRightIcon}
                onClick={() => setPageNumber(pageNumber + 1)}
                size="xs"
              />
            </Flex>

            <Flex flex="1" justify="flex-end">
              {pageSize && setPageSize && (
                <Flex align="center">
                  <Text fontSize="xs" textColor="secondary">
                    {t("Show", {
                      context:
                        "page size dropdown label before the number of rows",
                    })}
                  </Text>
                  <RedoButton
                    hierarchy="secondary"
                    IconTrailing={ChevronDown}
                    onClick={() =>
                      setPageSizeDropdownOpen(!pageSizeDropdownOpen)
                    }
                    ref={setPageSizeRef}
                    text={String(pageSize)}
                  />
                  <RedoListDropdown
                    dropdownAnchor={pageSizeRef}
                    dropdownOpen={pageSizeDropdownOpen}
                    items={pageSizeOptions}
                    itemSelected={({ item }) => setPageSize(item.value)}
                    selectedItems={pageSize}
                    setDropdownOpen={setPageSizeDropdownOpen}
                    size="xs"
                  />
                </Flex>
              )}
            </Flex>
          </>
        )}
      </Flex>
    );
  }),
);

export interface SaveViewBody {
  name: string;
  filters: AdvancedFilterData[];
  filterGroup?: FilterGroup;
  columns: ViewColumn[];
  sort: ViewSort | undefined;
  pageSize: number | undefined;
  _id?: string | null;
  position: number;
  showNotification?: boolean;
  hideWhenEmpty?: boolean;
}

export type DownloadTriggerRef<T> =
  | (({
      columnHeaders,
      rowToCsvColumns,
      rows,
    }: {
      columnHeaders?: string[];
      rowToCsvColumns?: (row: T) => string[][];
      rows?: T[];
    }) => void)
  | undefined;

export type TableProps2<T> = Omit<TableProps<T>, "columns"> & {
  downloadTriggerRef?: MutableRefObject<DownloadTriggerRef<T>>;
  columns: Column2<T>[];
  // Opt-in for relevance-ranked backends (OpenSearch _score): while a search is
  // active and the user hasn't chosen a column, results lead by relevance.
  relevanceSort?: boolean;
  isRowSelected?: (item: T) => boolean;
  isRowClickable?: (row: T, rowIndex: number) => boolean;
  /**
   * Row destination path; enables Cmd/Ctrl+click to open it in a new tab and an "Open in new tab" menu.
   * Return `undefined` to disable both for a row. `onContextMenu` overrides the built-in menu.
   */
  rowPath?: (record: T) => string | undefined;
  size?: RedoTableSize;
  embeddedTable?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  headerSideText?: string;
  /** Title on the toolbar row (left), not on a row above the controls. */
  inlineHeaderTitle?: boolean;
  stickyPageControl?: boolean;
  hidePageControl?: boolean;
  columnSelection?: boolean;
  columnSelectionStorageKey?: string;
  expandAllStorageKey?: string;
  saveView?: (body: SaveViewBody) => void;
  saveViewLoad?: LoadState<void>;
  advancedFilters?: AdvancedTableFilter[];
  initialAdvancedFilters?: AdvancedTableFilter[];
  refreshSymbol?: symbol;
  searchRefreshSymbol?: symbol;
  viewBeingEdited?: boolean;
  setViewBeingEdited?: (viewBeingEdited: boolean) => void;
  activeView?: viewTypes[keyof viewTypes];
  reservedViewNames?: Set<string>;
  onDeleteView?: (view: viewTypes[keyof viewTypes]) => void;
  onlySaveView?: boolean;
  viewNotificationsEnabled?: boolean;
  tableContentClassName?: string;
  focusSearchOnRender?: boolean;
  setPageSize?: (size: number) => void;
  validPageSizes?: ValidPageSize[];
  adjustableColumnWidths?: boolean;
  allowPinning?: boolean;
  /** When true, allows users to reorder Table2 columns by dragging headers */
  enableColumnReordering?: boolean;
  hideHeader?: boolean;
  putTabsInHeader?: boolean;
  onExportRequested?: (view: BaseView, search?: string) => Promise<void> | void;
  /** Hides "Save as new view" and "Update view" while, say, rows are selected. */
  hideViewActionButtons?: boolean;
  exportButtonLeftShift?: number;
  toolbarEndPaddingRight?: number;
  useVirtualTable?: boolean;
  /** Fixed height for the virtual table container. Overrides the default calculation based on viewport. */
  maxAvailableHeight?: number;
  /** When true, the virtual table fills its flex container instead of a height computed from the viewport. Use inside modals and drawers. */
  fillContainer?: boolean;
  /** The rows currently shown; `viewChanged` marks the empty surface a view switch produces. */
  surfaceData?: (items: T[], viewChanged: boolean) => void;
  customReportDropdownItems?: CustomReportDropdownItem[];
  expander?: ({ row }: { row: T }) => {
    numExpansions: number;
    expandable: boolean;
  };
  /**
   * Expander column position: "first" (default, leftmost) or "after-selection", right after the
   * `key === "selected"` checkbox column so the checkbox stays leftmost.
   */
  expanderPosition?: "first" | "after-selection";
  totalCountTooltip?: { text: string; threshold: number };
  getCsvColumns?: (displayedColumns: DisplayedColumn[]) => CsvColumn<T>[];
  aboveTableContent?: ReactNode;
  filterInfo?: ReactNode;
  filterButtonTrailingContent?: ReactNode;
  /** Enable the new filter groups UI with AND/OR logic */
  enableFilterGroups?: boolean;
  /**
   * Fall back to the legacy flat add-filter dropdown. The cascade is default.
   */
  legacyFilterMenu?: boolean;
  /**
   * Opt an `enableFilterGroups` table into that same cascade, committing pills
   * into the nested filter group instead of the flat filter list.
   */
  nestedFilterCascade?: boolean;
  /** Debounce time in ms for search input. Defaults to 200. */
  searchDebounceMs?: number;
  /** When false, the "Edit view" button is hidden. ANDed with internal conditions. Defaults to true. */
  showEditViewButton?: boolean;
  /**
   * When false, hides the explicit "Reset filters" button while preserving
   * per-filter removal controls. Defaults to true.
   */
  showResetFilters?: boolean;
  cardLayout?: boolean;
};

export interface CustomReportDropdownItem {
  pending: boolean;
  dropdownItem: RedoCommandMenuItem;
}

export async function downloadRowsAsCsv({
  filename,
  rows,
}: {
  filename: string;
  rows: string[][];
}) {
  const blob = new Blob([stringify(rows)], { type: "text/csv" });
  await downloadBlob(blob, `${filename}.csv`);
}

export async function downloadCsvForWholeTable<T>({
  abortSignal,
  filename,
  filters,
  fetcher,
  primaryFilter,
  search,
  sort,
  passThroughValues,
  appliedAdvancedFilters,
  appliedFilterGroup,
  columnHeaders,
  rowToCsvColumns,
}: {
  abortSignal?: AbortSignal;
  filename: string;
  filters: { [name: string]: string };
  fetcher: TableFetcher<T, any>;
  primaryFilter: string;
  search: string;
  sort: TableSort;
  passThroughValues: any;
  appliedAdvancedFilters: AdvancedTableFilter[];
  appliedFilterGroup?: FilterGroup;
  columnHeaders: string[];
  rowToCsvColumns: (row: T) => string[][];
}) {
  const result = [columnHeaders];
  let data: T[];
  if (fetcher.sync) {
    data = fetcher.data(
      primaryFilter,
      filters,
      search || undefined,
      search ? undefined : sort,
      undefined,
      undefined,
      passThroughValues,
      appliedAdvancedFilters,
      appliedFilterGroup,
    ).items;
  } else {
    const allData = await asyncIterableToArray({
      [Symbol.asyncIterator]: () =>
        fetcher.data(
          primaryFilter,
          filters,
          search || undefined,
          search ? undefined : sort,
          undefined,
          undefined,
          abortSignal,
          passThroughValues,
          appliedAdvancedFilters,
          appliedFilterGroup,
        ),
    });
    data = allData.flatMap((item) => item.items);
  }
  for (const record of data) {
    result.push(...rowToCsvColumns(record));
  }

  await downloadRowsAsCsv({ filename, rows: result });
}

export async function downloadCsvForGivenRows<T>({
  filename,
  rows,
  columnHeaders,
  rowToCsvColumns,
}: {
  filename: string;
  rows: T[];
  columnHeaders: string[];
  rowToCsvColumns: (row: T) => string[][];
}) {
  const result = [columnHeaders];
  for (const record of rows) {
    result.push(...rowToCsvColumns(record));
  }
  await downloadRowsAsCsv({ filename, rows: result });
}

export const ExportCsv = genericMemo(function ExportCsv<T>({
  csv,
  getCsvColumns,
  hideExportButton = false,
  displayedColumns,
  filename,
  filters,
  primaryFilter,
  fetcher,
  search,
  sort,
  passThroughValues,
  downloadTriggerRef,
  onDownloadStatusChange,
  appliedAdvancedFilters,
  appliedFilterGroup,
  customReportDropdownItems = [],
  onExportRequested,
  activeView,
  columnWidths,
  exportButtonLeftShift,
}: {
  csv?: CsvColumn<T>[];
  getCsvColumns?: (displayedColumns: DisplayedColumn[]) => CsvColumn<T>[];
  displayedColumns: DisplayedColumn[];
  filename: string;
  hideExportButton?: boolean;
  primaryFilter: string;
  filters: { [name: string]: string };
  fetcher: TableFetcher<T, any>;
  search: string;
  sort: TableSort;
  passThroughValues: any;
  downloadTriggerRef?: MutableRefObject<DownloadTriggerRef<T>>;
  onDownloadStatusChange?: (pending: boolean) => void;
  appliedAdvancedFilters: AdvancedTableFilter[];
  appliedFilterGroup?: FilterGroup;
  customReportDropdownItems?: CustomReportDropdownItem[];
  onExportRequested?: (view: BaseView, search?: string) => Promise<void> | void;
  activeView?: viewTypes[keyof viewTypes];
  columnWidths?: Record<string, number>;
  exportButtonLeftShift?: number;
}) {
  const csvColumns = getCsvColumns
    ? getCsvColumns(displayedColumns)
    : csv || [];

  const defaultColumnHeaders = csvColumns.map((column) => column.header);

  const defaultRowToCsvColumns = useHandler((row: T) => {
    return [csvColumns.map((column) => column.cell(row))];
  });

  const [load, trigger] = useTriggerLoadWithInput(
    async (
      {
        columnHeaders,
        rowToCsvColumns,
        rows,
      }: {
        columnHeaders?: string[];
        rowToCsvColumns?: (row: T) => string[][];
        rows?: T[];
      },
      signal,
    ) => {
      if (rows) {
        await downloadCsvForGivenRows({
          filename,
          rows,
          columnHeaders: columnHeaders ?? defaultColumnHeaders,
          rowToCsvColumns: rowToCsvColumns ?? defaultRowToCsvColumns,
        });
      } else {
        await downloadCsvForWholeTable({
          abortSignal: signal,
          columnHeaders: columnHeaders ?? defaultColumnHeaders,
          rowToCsvColumns: rowToCsvColumns ?? defaultRowToCsvColumns,
          filename,
          filters,
          fetcher,
          primaryFilter,
          search,
          sort,
          passThroughValues,
          appliedAdvancedFilters,
          appliedFilterGroup,
        });
      }
    },
  );

  const [exportPending, setExportPending] = useState(false);

  const onClick = useHandler(async () => {
    if (onExportRequested) {
      // Build current view state and pass to parent
      const currentView: BaseView = {
        ...(activeView ?? {
          table: "table2",
          name: "table2",
          filters: [],
          columns: [],
          position: 0,
        }),
        columns: displayedColumns.map((col) => ({
          key: col.key,
          hidden: !col.displayed,
          width: columnWidths?.[col.key] ?? 150,
        })),
        sort: sort ? { key: sort.key, direction: sort.direction } : undefined,
        filters: appliedAdvancedFilters.map((filter) => filter.data),
        filterGroup: appliedFilterGroup,
      };
      setExportPending(true);
      try {
        await onExportRequested(currentView, search || undefined);
      } finally {
        setExportPending(false);
      }
    } else {
      // Default client-side CSV download
      trigger({});
    }
  });
  const onTriggerDownload = useHandler(
    ({
      columnHeaders,
      rowToCsvColumns,
      rows,
    }: {
      columnHeaders?: string[];
      rowToCsvColumns?: (row: T) => string[][];
      rows?: T[];
    }) => trigger({ columnHeaders, rowToCsvColumns, rows }),
  );
  if (downloadTriggerRef) {
    downloadTriggerRef.current = onTriggerDownload;
  }

  useEffect(() => {
    if (onDownloadStatusChange) {
      onDownloadStatusChange(load.pending);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.pending]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownRef, setDropdownRef] = useState<HTMLButtonElement | null>(
    null,
  );

  const { t } = useTranslation();
  const exportButtonText = onExportRequested ? t("Export") : t("Export CSV");

  const dropdownItems: RedoCommandMenuItem[] = [
    { text: exportButtonText, onClick },
    ...customReportDropdownItems.map((item) => ({ ...item.dropdownItem })),
  ];

  const someCustomReportLoading = customReportDropdownItems.some(
    (item) => item.pending,
  );

  if (hideExportButton) {
    return null;
  }

  const buttonStyle = exportButtonLeftShift
    ? {
        position: "relative" as const,
        left: `-${exportButtonLeftShift}px`,
        transition: "left 200ms cubic-bezier(0, 0, 0.2, 1) 25ms",
      }
    : { transition: "left 200ms cubic-bezier(0, 0, 0.2, 1) 25ms" };

  // With onExportRequested, the button opens the parent's export flow while
  // downloadTriggerRef still allows the parent to trigger downloads.
  return (
    (!downloadTriggerRef || !!onExportRequested) &&
    (dropdownItems.length > 1 ? (
      <div data-web="ExportCsv" style={buttonStyle}>
        <RedoButtonDropdown
          dontRestrainIconWidth
          dropdownOpen={dropdownOpen}
          hierarchy="secondary"
          IconLeading={DownloadIconSvg}
          onClick={onClick}
          pending={load.pending || exportPending || someCustomReportLoading}
          ref={setDropdownRef}
          setDropdownOpen={setDropdownOpen}
          size="sm"
          text={exportButtonText}
          theme="normal"
        >
          <RedoCommandMenu
            anchor={dropdownRef}
            items={dropdownItems}
            open={dropdownOpen}
            setOpen={setDropdownOpen}
          />
        </RedoButtonDropdown>
      </div>
    ) : (
      <div data-web="ExportCsv" style={buttonStyle}>
        <RedoButton
          hierarchy="secondary"
          IconLeading={
            onExportRequested ? DownloadCloudIconSvg : DownloadIconSvg
          }
          onClick={onClick}
          pending={load.pending || exportPending}
          size="sm"
          text={onExportRequested ? undefined : exportButtonText}
          theme="normal"
        />
      </div>
    ))
  );
});

const Filters = memo(function Filters({
  filter,
  filterCounts,
  setFilter,
  filters,
  dynamicFilters = false,
  putTabsInHeader = false,
}: {
  filter: string;
  filterCounts?: { [key: string]: number };
  setFilter(key: string): void;
  filters: Filter[];
  dynamicFilters?: boolean;
  putTabsInHeader?: boolean;
}) {
  if (dynamicFilters && filterCounts) {
    filters = filters.filter((f) => (filterCounts[f.key] ?? 0) > 0);
  }

  const tabs = (
    <RedoHorizontalTabs
      drawBottomLine={false}
      mode="regular"
      pl="none"
      selectedTab={filter}
      setSelectedTab={setFilter}
      tabs={filters.map((f) => ({
        label: f.name,
        count: filterCounts?.[f.key],
        key: f.key,
      }))}
    />
  );

  if (putTabsInHeader) {
    return <TabsPortal>{tabs}</TabsPortal>;
  }

  return tabs;
});

export const DefaultLoadingContentMessage = memo(
  function DefaultLoadingContentMessage({
    borderless,
  }: {
    borderless?: boolean;
  }) {
    const { t } = useTranslation();
    return (
      <Flex
        align="center"
        className={
          borderless
            ? table2Css.borderlessSpinnerContainer
            : table2Css.spinnerContainer
        }
        grow={1}
        justify="center"
        py="6xl"
      >
        <Flex>
          <Flex align="center" gap="md">
            <Text fontSize="xs" textColor="tertiary">
              {t("Loading...")}
            </Text>
            <Flex className={table2Css.spinner} mr="2xl">
              <Spinner />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

export const DefaultEmptyContentMessage = memo(
  function DefaultEmptyContentMessage({
    borderless,
  }: {
    borderless?: boolean;
  }) {
    const { t } = useTranslation();
    return (
      <Flex
        align="center"
        className={
          borderless
            ? table2Css.borderlessSpinnerContainer
            : table2Css.spinnerContainer
        }
        grow={1}
        justify="center"
        py="6xl"
      >
        <Flex>
          <Flex align="center" gap="md">
            <Text fontWeight="medium" textColor="tertiary">
              {t("No matching records")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

type TableContentProps2<T> = Omit<TableContentProps<T>, "columns"> & {
  columns: Column2<T>[];
  dataScopeKey: string | null;
  relevanceSort?: boolean;
  relevanceActive?: boolean;
  size?: RedoTableSize;
  LoadingContentMessage?: ReactElement;
  rowPath?: (record: T) => string | undefined;
  appliedAdvancedFilters?: AdvancedTableFilter[];
  appliedFilterGroup?: FilterGroup;
  activeView?: viewTypes;
  className?: string;
  isRowSelected?: (item: T) => boolean;
  isRowClickable?: (row: T, rowIndex: number) => boolean;
  adjustableColumnWidths?: boolean;
  allowPinning?: boolean;
  enableColumnReordering?: boolean;
  columnWidths: Record<string, number>;
  setColumnWidths: (columnWidths: Record<string, number>) => void;
  columnPins: Record<string, boolean>;
  setColumnPins: (columnPins: Record<string, boolean>) => void;
  displayedColumns: DisplayedColumn[];
  setDisplayedColumns: (columns: DisplayedColumn[]) => void;
  footerRef?: RefObject<HTMLDivElement>;
  cardLayout?: boolean;
  useVirtualTable?: boolean;
  maxAvailableHeight?: number;
  fillContainer?: boolean;
  surfaceData?: (items: T[], viewChanged: boolean) => void;
  expandedRows?: Record<number, boolean>;
  toggleExpandedRow?: (rowIndex: number) => void;
  expander?: ({ row }: { row: T }) => {
    numExpansions: number;
    expandable: boolean;
  };
};

const getNumericWidth = (
  width: React.CSSProperties["width"] | undefined,
): number | undefined => {
  if (typeof width === "number") {
    return width;
  }
  if (typeof width === "string") {
    const parsedWidth = parseInt(width, 10);
    return Number.isNaN(parsedWidth) ? undefined : parsedWidth;
  }
  return undefined;
};

const getColumnInitialWidth = <DATA,>(column: Column2<DATA>): number => {
  if (column.widthMode?.type === "fixed") {
    return getNumericWidth(column.widthMode.width) ?? column.width;
  }
  if (column.widthMode?.type === "fill") {
    return getNumericWidth(column.widthMode.minLength) ?? column.width;
  }
  return column.width;
};

const canResizeColumnWidth = <DATA,>({
  adjustableColumnWidths,
  column,
}: {
  adjustableColumnWidths: boolean | undefined;
  column: Column2<DATA>;
}): boolean => {
  return Boolean(
    adjustableColumnWidths &&
    !column.disableColumnResize &&
    (column.enableColumnResize ||
      !column.widthMode ||
      column.widthMode.type === "fill"),
  );
};

const NonAdjustableBorder = ({ held }: { held: boolean }) => {
  return (
    <div
      className={classnames(
        redoTableHeaderCellsCss.columnResizerIndicator,
        held && redoTableHeaderCellsCss.columnResizerIndicatorActive,
      )}
    />
  );
};

export interface YieldType<T> {
  items: T[];
  refresh?: ((signal: AbortSignal) => Promise<T[]>) | undefined;
  aborted?: boolean;
}
function TableContentComponent<T>(
  {
    columns,
    dataScopeKey,
    columnWidths,
    setColumnWidths,
    columnPins,
    setColumnPins,
    displayedColumns,
    setDisplayedColumns,
    allowPinning,
    enableColumnReordering,
    fetcher,
    primaryFilter,
    filters,
    onRowClick,
    onRowHovered,
    sort,
    search,
    pageNumber,
    setSort,
    relevanceSort,
    relevanceActive,
    passThroughValues,
    alwaysLoadTable = false,
    onQuickDownload,
    scrollable = false,
    theme = TableTheme.COMPACT,
    scrollAreaRef,
    header,
    pageSize,
    EmptyContentMessage = <DefaultEmptyContentMessage />,
    onContextMenu,
    rowPath,
    size: size = "md",
    cardLayout = false,
    LoadingContentMessage = <DefaultLoadingContentMessage />,
    refreshSymbol,
    appliedAdvancedFilters,
    appliedFilterGroup,
    isRowSelected,
    isRowClickable,
    activeView,
    className,
    adjustableColumnWidths,
    footerRef,
    useVirtualTable,
    maxAvailableHeight,
    fillContainer,
    surfaceData,
    expander,
    expandedRows,
    toggleExpandedRow,
  }: TableContentProps2<T>,
  ref: ForwardedRef<TableRef<T>>,
) {
  const { t } = useTranslation();
  const headerSort: TableSort = relevanceActive
    ? { key: RELEVANCE_SORT_KEY, direction: SortDirection.DESC }
    : sort;
  const fetchSort = relevanceSort
    ? relevanceActive
      ? undefined
      : sort
    : search
      ? undefined
      : sort;

  const scrolled = useScrolled(scrollAreaRef.current);
  const [itemsState, setItemsState] = useState<{
    dataScopeKey: string | null;
    items: T[];
  }>(() => ({ dataScopeKey, items: [] }));
  const items = itemsState.items;
  const dataScopeChanged = itemsState.dataScopeKey !== dataScopeKey;
  const setItems = useCallback(
    (nextItems: React.SetStateAction<T[]>) => {
      setItemsState((current) => {
        const currentItems =
          current.dataScopeKey === dataScopeKey ? current.items : [];
        const resolvedItems =
          typeof nextItems === "function" ? nextItems(currentItems) : nextItems;
        return { dataScopeKey, items: resolvedItems };
      });
    },
    [dataScopeKey],
  );
  const [iterator, setIterator] = useState<AsyncIterator<YieldType<T>>>();
  const [started, setStarted] = useState(false);
  const [pending, setPending] = useState(false);
  const [finished, setFinished] = useState(false);
  const [refresh, setRefresh] =
    useState<(signal: AbortSignal) => Promise<T[]>>();

  const abortControllerRef = useRef<AbortController>(new AbortController());
  const [heldBorderKey, setHeldBorderKey] = useState<string | null>(null);
  const {
    activeColumnKey,
    enabled: columnReorderingEnabled,
    renderColumnReorderContext,
  } = useTable2ColumnReordering({
    columns,
    columnPins,
    displayedColumns,
    enableColumnReordering,
    setColumnPins,
    setDisplayedColumns,
  });
  const activeDragColumn = useMemo(
    () =>
      activeColumnKey
        ? (columns.find((column) => column.key === activeColumnKey) ?? null)
        : null,
    [activeColumnKey, columns],
  );

  const processedColumns = useMemo(
    () =>
      columns.map((column) => {
        const isPinned = columnPins?.[column.key] ?? false;
        const canAdjustColumnWidth = canResizeColumnWidth({
          adjustableColumnWidths,
          column,
        });
        const columnInitialWidth = getColumnInitialWidth(column);
        const adjustedColumnWidth =
          canAdjustColumnWidth || !column.widthMode
            ? (columnWidths?.[column.key] ?? columnInitialWidth)
            : columnInitialWidth;
        const widthMode = column.widthMode
          ? canAdjustColumnWidth && column.widthMode.type === "fixed"
            ? { ...column.widthMode, width: adjustedColumnWidth }
            : canAdjustColumnWidth && column.widthMode.type === "fill"
              ? { ...column.widthMode, minLength: adjustedColumnWidth }
              : column.widthMode
          : { type: "fill" as const, minLength: adjustedColumnWidth };
        return {
          ...column,
          sticky: isPinned || !!column.sticky,
          renderNormalCell: canAdjustColumnWidth
            ? (props: RedoTableCellProps<T>) => {
                return (
                  <>
                    <div className={table2Css.normalCellWithAdjustableBorder}>
                      {column.renderNormalCell(props)}
                    </div>
                    <NonAdjustableBorder held={column.key === heldBorderKey} />
                  </>
                );
              }
            : column.renderNormalCell,
          widthMode,
          renderHeaderCell: () => {
            function onSort(direction: SortDirection) {
              setSort({ direction, key: column.key });
            }
            function onTogglePin() {
              setColumnPins({ ...columnPins, [column.key]: !isPinned });
            }
            const headerCell = (
              <Flex
                align="stretch"
                className={classnames(
                  table2Css.columnHeaderSurface,
                  column.key === heldBorderKey &&
                    table2Css.columnHeaderSurfaceResizing,
                )}
                data-table2-resizing={
                  column.key === heldBorderKey ? "true" : undefined
                }
                gap="none"
                height="full"
                minW="none"
                position="relative"
                w="full"
              >
                <ColumnHeader
                  defaultDirection={column.sort}
                  direction={
                    headerSort.key === column.key ? headerSort.direction : null
                  }
                  sort={column.sort !== undefined ? onSort : undefined}
                  theme={theme}
                  width={adjustedColumnWidth}
                >
                  {column}
                </ColumnHeader>
                {allowPinning && !column.disableStickiness && (
                  <Flex
                    basis={
                      canAdjustColumnWidth
                        ? "var(--redo-spacing-5xl)"
                        : "var(--redo-spacing-2xl)"
                    }
                    className={classnames(table2Css.pinButtonContainer)}
                    onClick={onTogglePin}
                    pl="xs"
                    pr={canAdjustColumnWidth ? "3xl" : "xs"}
                    title={isPinned ? t("Unpin column") : t("Pin column")}
                  >
                    <div
                      className={classnames(
                        table2Css.pinIcon,
                        isPinned && table2Css.pinIconActive,
                      )}
                    >
                      {isPinned ? <PinFilledIcon /> : <PinIcon />}
                    </div>
                  </Flex>
                )}
                {canAdjustColumnWidth && (
                  <RedoTableColumnResizer
                    held={column.key === heldBorderKey}
                    onAdjustColumnWidth={({ widthDif }) => {
                      setColumnWidths({
                        ...columnWidths,
                        [column.key]: getResizedColumnWidth({
                          currentWidth:
                            columnWidths?.[column.key] ?? columnInitialWidth,
                          widthDif,
                        }),
                      });
                    }}
                    setHeld={(held: boolean) => {
                      if (held) {
                        setHeldBorderKey(column.key);
                      } else {
                        setHeldBorderKey(null);
                      }
                    }}
                  />
                )}
              </Flex>
            );

            if (!columnReorderingEnabled || !isColumnReorderable(column)) {
              return headerCell;
            }

            return (
              <SortableItem asChild asHandle value={column.key}>
                <Flex
                  align="stretch"
                  className={table2Css.draggableColumnHeader}
                  height="full"
                  minW="none"
                  position="relative"
                  w="full"
                >
                  {headerCell}
                </Flex>
              </SortableItem>
            );
          },
        };
      }),
    [
      setColumnWidths,
      columnWidths,
      columns,
      columnPins,
      setColumnPins,
      allowPinning,
      headerSort.key,
      headerSort.direction,
      setSort,
      theme,
      adjustableColumnWidths,
      heldBorderKey,
      setHeldBorderKey,
      columnReorderingEnabled,
      t,
    ],
  );

  /**
   * Only fetch from the latest iterator: an aborted one can report "done" and set `finished`, skipping
   * the newer iterator. A bandaid for async code in `useEffect` setting shared state.
   */
  const latestIteratorRef = useRef(iterator);

  useEffect(() => {
    if (fetcher.sync) {
      const result = fetcher.data(
        primaryFilter,
        filters,
        search || undefined,
        fetchSort,
        pageSize || DEFAULT_PAGE_SIZE,
        pageNumber,
        passThroughValues,
        appliedAdvancedFilters,
        appliedFilterGroup,
      );

      setItems(result.items);
      return;
    }
    abortControllerRef.current?.abort();
    const newAbort = new AbortController();
    const signal = newAbort.signal;
    const newIterator = fetcher.data(
      primaryFilter,
      filters,
      search || undefined,
      fetchSort,
      pageSize || DEFAULT_PAGE_SIZE,
      pageNumber,
      signal,
      passThroughValues,
      appliedAdvancedFilters,
      appliedFilterGroup,
    );
    latestIteratorRef.current = newIterator;
    setIterator(newIterator);
    setPending(false);
    setFinished(false);
    setStarted(false);
    setItems([]);
    abortControllerRef.current = newAbort;
    if (iterator?.return) {
      void iterator.return();
    }

    // Make sure if we unmount this component, we abort the in flight requests
    return () => {
      newAbort.abort();
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetcher,
    primaryFilter,
    search,
    sort,
    relevanceActive,
    relevanceSort,
    filters,
    appliedAdvancedFilters,
    appliedFilterGroup,
    passThroughValues,
    pageNumber,
    refreshSymbol,
    pageSize,
    dataScopeKey,
  ]);

  useEffect(() => {
    async function loadMoreIfNecessary() {
      if (
        (scrolled.bottom || alwaysLoadTable || !started) &&
        !pending &&
        !finished &&
        iterator &&
        iterator === latestIteratorRef.current
      ) {
        setStarted(true);
        setPending(true);
        let done: boolean;
        let value: YieldType<T>;
        let aborted: boolean;
        try {
          const results = await iterator.next();
          done = results.done || false;
          value = results.value;
          aborted = value?.aborted || false;
          if (aborted) {
            return;
          } // Pending has already been restored to false
        } catch (e) {
          setPending(false);
          throw e;
        }
        setPending(false);
        if (iterator !== latestIteratorRef.current) {
          return;
        }

        if (done) {
          if (iterator.return) {
            await iterator.return();
          }
          if (!aborted) {
            setFinished(true);
          }
          return;
        }
        setRefresh(() => value.refresh);
        setItems((items) => [...items, ...value.items]);
      }
    }
    void loadMoreIfNecessary();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iterator, scrolled.bottom, pending, finished, started]);

  useEffect(() => {
    surfaceData?.(dataScopeChanged ? [] : items, dataScopeChanged);
  }, [surfaceData, items, dataScopeKey, dataScopeChanged]);

  const refreshData = async () => {
    if (refresh) {
      const reloadedItems = await refresh(abortControllerRef.current.signal);
      setItems(reloadedItems);
    }
  };

  const removeItem = (
    currentItem: T,
    areEqual: (item1: T, item2: T) => boolean,
  ) => {
    const index = items.findIndex((item) => areEqual(item, currentItem));
    if (index === -1) {
      return;
    }
    setItems((items) => {
      const newItems = [...items];
      newItems.splice(index, 1);
      return newItems;
    });
  };

  const externalRefresh = async (setItemsCallback: (items: T[]) => void) => {
    if (refresh) {
      const reloadedItems = await refresh(abortControllerRef.current.signal);
      setItemsCallback(reloadedItems);
    }
  };

  const externalSetItems = (items: T[]) => {
    setItems(items);
  };

  useImperativeHandle<TableRef<T>, TableRef<T>>(ref, () => ({
    items: dataScopeChanged ? [] : items,
    refresh: refreshData,
    removeItem,
    externalRefresh,
    externalSetItems,
    toggleExpandedRow,
  }));

  const itemsToShow = dataScopeChanged ? [] : items;
  const waitingForFirstPage = !fetcher.sync && !started && !finished;
  const showLoadingState = dataScopeChanged || pending || waitingForFirstPage;

  const shouldShowAnEmptyMessage = !showLoadingState && !itemsToShow.length;

  // const width = sumBy(columns, (column) => column.width);
  const rowsSelected =
    isRowSelected && itemsToShow.map((item) => isRowSelected(item));

  const [contextMenuPos, setContextMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<HTMLDivElement | null>(null);
  const [contextMenuRecord, setContextMenuRecord] = useState<T | null>(null);

  const openRowInNewTab = useHandler((row: T) => {
    const path = rowPath?.(row);
    if (!path) {
      return;
    }
    const base = new URL(location.href);
    base.search = "";
    if (!base.pathname.endsWith("/")) {
      base.pathname += "/";
    }
    open(new URL(path, base), "_blank");
  });

  const handleRowClick =
    onRowClick || rowPath
      ? (row: T, rowIndex: number, e: MouseEvent, expansionIndex?: number) => {
          if (rowPath && isControlKeyPressed(e) && rowPath(row)) {
            openRowInNewTab(row);
            return;
          }
          onRowClick?.(
            row,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            e as MouseEvent<HTMLTableRowElement>,
            rowIndex,
            expansionIndex,
          );
        }
      : undefined;

  const handleContextMenu = (
    row: T,
    e: MouseEvent<HTMLTableRowElement>,
    rowIndex: number,
  ) => {
    if (onContextMenu) {
      onContextMenu(row, e as MouseEvent<HTMLTableRowElement>, rowIndex);
      return;
    }
    if (rowPath && rowPath(row)) {
      e.preventDefault();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuRecord(row);
    }
  };

  const closeContextMenu = useHandler(() => {
    setContextMenuPos(null);
    setContextMenuAnchor(null);
    setContextMenuRecord(null);
  });

  // Close the context menu on scroll; its viewport-relative anchor would otherwise float over another row.
  // Capture phase catches scrolls in nested containers, since scroll events don't bubble.
  useEffect(() => {
    if (!contextMenuPos) {
      return;
    }
    const handleScroll = () => closeContextMenu();
    window.addEventListener("scroll", handleScroll, { capture: true });
    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [contextMenuPos, closeContextMenu]);

  const handleOpenInNewTabFromMenu = useHandler(() => {
    if (contextMenuRecord) {
      openRowInNewTab(contextMenuRecord);
    }
    closeContextMenu();
  });

  const rowExpansions =
    expandedRows && expander
      ? Object.entries(expandedRows).reduce(
          (acc, [rowIndex, expanded]) => {
            const idx = parseInt(rowIndex);
            const row = itemsToShow[idx];
            if (row !== undefined) {
              const { numExpansions, expandable } = expander({ row });
              acc[idx] = { expanded: expanded && expandable, numExpansions };
            }
            return acc;
          },
          {} as Record<number, { expanded: boolean; numExpansions: number }>,
        )
      : undefined;

  const hasAggregation = columns.some((col) => col.aggregation != null);
  const aggregationCells = hasAggregation ? (
    <AggregationCells
      columns={columns}
      columnWidths={columnWidths}
      items={itemsToShow}
    />
  ) : null;

  const aggregationRow = hasAggregation ? (
    <AggregationRow
      columns={columns}
      columnWidths={columnWidths}
      items={itemsToShow}
    />
  ) : null;

  const resolvedLoadingContent = cardLayout ? (
    <RedoTableLoadingStateContent />
  ) : (
    LoadingContentMessage
  );
  const resolvedEmptyContent = shouldShowAnEmptyMessage ? (
    cardLayout ? (
      EmptyContentMessage
    ) : (
      <Flex grow={1}>{EmptyContentMessage}</Flex>
    )
  ) : null;

  const table = useVirtualTable ? (
    <RedoVirtualTable
      additionalClasses={cardLayout ? redoTableCss.standaloneTable : undefined}
      aggregationRow={aggregationCells}
      emptyStateContent={resolvedEmptyContent}
      fillContainer={fillContainer}
      fillStandaloneBody={cardLayout}
      footerRef={footerRef}
      isRowClickable={isRowClickable}
      layout={{
        columns: processedColumns.map((processedColumn) => {
          return { ...processedColumn, alignment: undefined };
        }),
        headerColor: "grey",
        standalone: cardLayout,
      }}
      loadingStateContent={showLoadingState ? resolvedLoadingContent : null}
      maxAvailableHeight={maxAvailableHeight}
      onContextMenu={handleContextMenu}
      onRowClick={handleRowClick}
      onRowHovered={onRowHovered}
      rowsExpansions={rowExpansions}
      rowsOfData={itemsToShow}
      rowsSelected={rowsSelected}
      size={size}
    />
  ) : (
    <RedoTable
      aggregationRow={aggregationRow}
      emptyStateContent={resolvedEmptyContent}
      fillStandaloneBody={cardLayout}
      isRowClickable={isRowClickable}
      layout={{
        columns: processedColumns.map((processedColumn) => {
          return { ...processedColumn, alignment: undefined };
        }),
        headerColor: "grey",
        standalone: cardLayout,
      }}
      loadingStateContent={showLoadingState ? resolvedLoadingContent : null}
      onContextMenu={handleContextMenu}
      onRowClick={handleRowClick}
      onRowHovered={onRowHovered}
      rowsExpansions={rowExpansions}
      rowsOfData={itemsToShow}
      rowsSelected={rowsSelected}
      size={size}
    />
  );

  const tableWithColumnReordering = renderColumnReorderContext(
    table,
    activeDragColumn ? (
      <ColumnHeaderDragOverlay
        allowPinning={allowPinning}
        column={activeDragColumn}
        direction={
          headerSort.key === activeDragColumn.key ? headerSort.direction : null
        }
        isPinned={columnPins?.[activeDragColumn.key] ?? false}
        theme={theme}
        width={
          columnWidths?.[activeDragColumn.key] ??
          getColumnInitialWidth(activeDragColumn)
        }
      />
    ) : null,
  );

  return (
    <div
      className={classnames(
        table2Css.tableContentInner,
        activeColumnKey && table2Css.tableContentDraggingColumns,
        fillContainer && table2Css.tableContentInnerFill,
        className,
      )}
    >
      {header && <div className={tableCss.headerSpacer} />}
      {tableWithColumnReordering}
      {rowPath && contextMenuPos && (
        <div
          className={table2Css.contextMenuAnchor}
          ref={setContextMenuAnchor}
          style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
        />
      )}
      {rowPath && (
        <RedoCommandMenu
          anchor={contextMenuAnchor}
          items={[
            {
              Icon: LinkExternal02Icon,
              text: t("Open in new tab"),
              onClick: handleOpenInNewTabFromMenu,
            },
          ]}
          open={!!contextMenuAnchor}
          placement="bottom-start"
          setOpen={(value) => {
            if (!value) {
              closeContextMenu();
            }
          }}
        />
      )}
    </div>
  );
}

const TableContent = genericMemo(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  forwardRef(TableContentComponent) as <T>(
    props: TableContentProps2<T> & { ref?: React.ForwardedRef<TableRef<T>> },
  ) => ReturnType<typeof TableContentComponent>,
);

interface ColumnHeaderDragOverlayProps {
  allowPinning?: boolean;
  column: Column2<any>;
  direction: SortDirection | null;
  isPinned: boolean;
  theme?: TableTheme;
  width: number;
}

const ColumnHeaderDragOverlay = memo(function ColumnHeaderDragOverlay({
  allowPinning,
  column,
  direction,
  isPinned,
  theme,
  width,
}: ColumnHeaderDragOverlayProps) {
  const overlayWidthStyleProps = useMemo<{ style: React.CSSProperties }>(
    () => ({ style: { width } }),
    [width],
  );

  return (
    <Flex
      className={table2Css.draggableColumnHeaderOverlay}
      gap="none"
      height="full"
      {...overlayWidthStyleProps}
    >
      <Flex
        align="stretch"
        className={classnames(
          table2Css.columnHeaderSurface,
          table2Css.columnHeaderSurfaceDragOverlay,
        )}
        gap="none"
        height="full"
        minW="none"
        position="relative"
        w="full"
      >
        <ColumnHeader
          defaultDirection={column.sort}
          direction={direction}
          theme={theme}
          width={width}
        >
          {column}
        </ColumnHeader>
        {allowPinning && !column.disableStickiness && (
          <Flex
            basis="var(--redo-spacing-2xl)"
            className={classnames(table2Css.pinButtonContainer)}
            pl="xs"
            pr="xs"
          >
            <div
              className={classnames(table2Css.pinIcon, table2Css.pinIconActive)}
            >
              {isPinned ? <PinFilledIcon /> : <PinIcon />}
            </div>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

interface ColumnHeaderProps {
  defaultDirection?: SortDirection;
  direction: SortDirection | null;
  children: Column<any>;
  sort?(direction: SortDirection): void;
  width: number;
  theme?: TableTheme;
}

type ColumnHeaderProps2 = Omit<ColumnHeaderProps, "children"> & {
  children: Column2<any>;
};

const ColumnHeader = memo(function ColumnHeader({
  defaultDirection = SortDirection.ASC,
  direction,
  children,
  sort,
  width,
  theme = TableTheme.COMPACT,
}: ColumnHeaderProps2) {
  const onClick = useHandler(() => {
    sort?.(
      direction == null
        ? defaultDirection
        : direction === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC,
    );
  });

  if (typeof children.title === "string") {
    let arrowIcon;
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (direction) {
      case SortDirection.ASC:
        arrowIcon = StandardRedoTableHeaderCellArrowMode.ASCENDING;
        break;
      case SortDirection.DESC:
        arrowIcon = StandardRedoTableHeaderCellArrowMode.DESCENDING;
        break;
      default:
        arrowIcon = StandardRedoTableHeaderCellArrowMode.NO_ARROW;
    }

    return (
      <StandardRedoTableHeaderCell
        alignment={children.alignment}
        arrowIcon={arrowIcon}
        headerText={children.title}
        helpIconTooltip={children.headerTooltip}
        onSortClick={sort ? onClick : undefined}
      />
    );
  }

  return (
    <div className={redoTableHeaderCellsCss.headerCell}>{children.title}</div>
  );
});
